import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
    COMPONENT_TERMINAL_BUILD_STAGES,
    COMPONENT_JOB_CONCURRENCY_POLICIES,
    COMPONENT_JOB_ENGINE_POLICIES,
    COMPONENT_JOB_SCHEDULE_PRESETS,
    COMPONENT_TERMINAL_RUN_STAGES,
    COMPONENT_WEBHOOK_EVENTS,
    cancelRun,
    type ComponentHandlerCallResult,
    createDefaultComponentJobSettings,
    deleteWebhook,
    disableSchedule,
    downloadRunLog,
    getBuildStatus,
    getComponentBuildAttemptPaths,
    getComponentJobBuildPaths,
    getComponentJobCompanionPaths,
    getComponentJobRootPath,
    getComponentRunPaths,
    getRunStatus,
    isBlankComponentJobSource,
    parseComponentJobPath,
    parseComponentJobConfig,
    prepareBuild,
    resolveComponentJobScheduleCron,
    runServer,
    schedule,
    serializeComponentJobConfig,
    tailRunLog,
    upsertWebhook,
    getWebhooks,
    getWebhookDeliveries,
    getAppIacStatus,
    type ComponentJobServerRunner,
    type ComponentBuildStatusRecord,
    type ComponentJobConcurrencyPolicy,
    type ComponentJobEnginePolicy,
    type ComponentJobScaffold,
    type ComponentJobSchedulePreset,
    type ComponentJobSettings,
    type ComponentJobStarterTemplate,
    type ComponentRunStatusRecord,
    type ComponentServerEngine,
    type ComponentWebhookEvent,
    type ComponentWebhookRegistrationRecord,
    type ComponentWebhookDeliveryRecord,
    type ComponentWebhookWithDeliveriesRecord,
    type SecureBlobAppIacStatusCard,
    type SecureBlobAppIacStatusResult,
} from '@secureblob/sdk/componentJobs'
import type { FileStat } from '../engine/types'
import type { McpConnectionEntry } from '../engine/mcp/types'
import { generateConfigConnectionToken } from '../engine/mcp/crypto'
import { useEngineManager, useFileSystem, useRegistry, useWorkspace, useWorkspaceMetadata } from '../hooks/useWorkspace'
import { formatExactTime, timeAgo } from '../utils/writerUtils'
import { useToast } from './Toast'
import {
    callHandler as callComponentJobHandler,
    createComponentJobsClientOptions,
    createComponentRunnerBootstrap,
    createComponentSourceSnapshot,
    resolveComponentJobsApiBaseUrl,
} from './componentJobs/apiClient'
import {
    materializeBuildArtifactToWorkspace,
    materializeFailedBuildStatusToWorkspace,
    materializeRunStatusToWorkspace,
    type MaterializedRunEvent,
} from './componentJobs/controlPlaneMaterialization'
import {
    captureComponentJobRunProbe,
    createComponentJobRunProbeCaptureMarker,
    persistComponentJobBuildProbe,
    persistComponentJobRunProbe,
    persistIncrementalRuntimeTrace,
    LIVE_RUNTIME_TRACE_FILENAME,
    type ComponentJobRunProbe,
    type ComponentJobRunProbeCaptureMarker,
} from './componentJobs/runProbe'
import type { RuntimeEventEntry } from '../testing/runtimeEventTrace'
import { TranscriptMessage } from './agent/TranscriptMessage'
import {
    applyStarterScaffoldToJobRoot,
    createSourceCompatibleComponentJobScaffold,
    listComponentJobStarterScaffoldsForJobRoot,
    listComponentJobStarterScaffoldsForSourceFile,
} from './componentJobs/starterScaffolds'
import { resolveComponentJobSourcePath } from './componentJobs/jobHistory'
import { ensureComponentJobServerRunner } from './componentJobs/serverRunner'
import type { Attachment, FileModification, ResolvedRef, TranscriptEvent } from '../engine/agentTypes'
import './AgentPanel.css'
import './ComponentJobPanel.css'

interface ComponentJobPanelProps {
    path: string
    onOpenFile?: (path: string) => void
    onOpenFileInEditMode?: (path: string) => void
    onEditFile?: () => void
    canWrite?: boolean
}

interface RunHistoryItem {
    runId: string
    metadataPath: string
    startedAt: number
    startedAtLabel: string
    status: string
    statusLabel: string
    target: string
    engine?: string
    summary?: string
    artifactId?: string
    stdoutPath?: string
    stderrPath?: string
    eventsPath?: string
    httpPath?: string
    mcpPath?: string
    outputFileCount: number
}

interface BuildHistoryItem {
    artifactId: string
    manifestPath: string
    createdAt: number
    createdAtLabel: string
    status: string
    statusLabel: string
    recommendedServerEngine?: string
    componentWasmPath?: string
    buildLogPath?: string
    sourceSnapshot?: string
    buildTarget?: string
    entrypoints: string[]
    worldVersion?: string
    artifactFiles: BuildArtifactFileItem[]
    totalFileBytes: number | null
}

interface BuildArtifactFileItem {
    path: string
    workspacePath: string
    size?: number
    sha256?: string
    available: boolean
}

interface FailedBuildAttemptItem {
    buildId: string
    metadataPath: string
    createdAt: number
    createdAtLabel: string
    status: string
    statusLabel: string
    summary?: string
    error?: string
    buildLogPath?: string
    probePath?: string
}

type RunInspectorTabId = 'overview' | 'stdout' | 'stderr' | 'events' | 'http' | 'mcp' | 'probe' | 'transcript' | 'outputs' | 'handlers' | 'deliveries' | 'live-runtime-trace'

interface RunInspectorTab {
    id: RunInspectorTabId
    label: string
}

interface RunOutputFile {
    path: string
    label: string
    mtime: number
}

interface RunTranscriptSession {
    path: string
    label: string
    sessionId: string
    mtime: number
}

interface RunTranscriptCostInfo {
    tokensIn: number
    tokensOut: number
    estimatedCostUsd: number
}

interface RunTranscriptRecord {
    schema: 'chat-transcript/v2'
    sessionId: string
    status: string
    agentPath: string | null
    agentConfigPath: string | null
    agentName: string | null
    model: string | null
    providerType: string | null
    providerApiMode: string | null
    updatedAt: number | null
    cost: RunTranscriptCostInfo | null
    runId: string | null
    sourcePath: string | null
    artifactId: string | null
    attachmentCount: number
    filesModified: FileModification[]
    uiEvents: TranscriptEvent[]
}

type RunLogTabId = 'stdout' | 'stderr' | 'events' | 'http' | 'mcp'
type RunLogName = 'stdout.log' | 'stderr.log' | 'events.jsonl' | 'http.jsonl' | 'mcp.jsonl'

interface JsonObject {
    [key: string]: unknown
}

interface RunMaterializationState {
    startedAt: string
    events: MaterializedRunEvent[]
}

interface HandlerInvocationHistoryItem {
    id: string
    startedAt: number
    statusCode: number
    durationMs: number
    inputJson: string | null
    inputSummary: string
    runId?: string
    output?: unknown
    message?: string
    errorCode?: string
}

interface ComponentJobSettingsDialogProps {
    sourceName: string
    initialSettings: ComponentJobSettings
    isSaving: boolean
    onClose: () => void
    onSave: (settings: ComponentJobSettings) => void | Promise<void>
}

interface ComponentJobStarterDialogProps {
    sourceName: string
    isFolderFirstJob: boolean
    scaffolds: readonly ComponentJobScaffold[]
    isApplying: boolean
    onClose: () => void
    onApply: (template: ComponentJobStarterTemplate) => void
}

interface ComponentJobConfirmState {
    title: string
    message: string
    confirmLabel: string
    onConfirm: () => void
}

interface ComponentWebhookFormState {
    callbackUrl: string
    secret: string
    events: ComponentWebhookEvent[]
}

interface AppIacStatusCredential {
    token: string
    label: string
    configBlobId?: string
    source: 'server-runner' | 'mcp-connection'
}

const APP_IAC_MANIFEST_PATH = '/.secureblob/iac/app-manifest.json'
const APP_IAC_MAILBOX_ROOT = '/agentcommunication'
const HANDLER_INPUT_MAX_BYTES = 1024 * 1024
const UTF8_ENCODER = new TextEncoder()

const SCHEDULE_PRESET_LABELS: Record<ComponentJobSchedulePreset, string> = {
    manual: 'Manual only',
    'every-5-min': 'Every 5 min',
    hourly: 'Hourly',
    daily: 'Daily',
    custom: 'Custom cron',
}

const ENGINE_POLICY_LABELS: Record<ComponentJobEnginePolicy, string> = {
    auto: 'Auto',
    'prefer-wasmtime': 'Prefer Wasmtime',
}

const CONCURRENCY_POLICY_LABELS: Record<ComponentJobConcurrencyPolicy, string> = {
    'skip-if-running': 'Skip if already running',
    queue: 'Queue',
    replace: 'Cancel previous and replace',
}

const WEBHOOK_EVENT_LABELS: Record<ComponentWebhookEvent, string> = {
    'run.cancelled': 'Run cancelled',
    'run.completed': 'Run completed',
    'run.failed': 'Run failed',
}

const BUILD_POLL_INTERVAL_MS = 900
const RUN_POLL_INTERVAL_MS = 1000
const RUN_LOG_TAB_TO_NAME: Record<RunLogTabId, RunLogName> = {
    stdout: 'stdout.log',
    stderr: 'stderr.log',
    events: 'events.jsonl',
    http: 'http.jsonl',
    mcp: 'mcp.jsonl',
}

interface TimingWaterfallEntry {
    dnsMs: number | null
    connectMs: number | null
    tlsMs: number | null
    ttfbMs: number | null
    downloadMs: number | null
    totalMs: number
    /** §254: SQLite write-lane page count (for sqlite-write-lane entries). */
    pageCount?: number
    /** §254: SQLite write-lane mode label (for sqlite-write-lane entries). */
    mode?: string
}

interface StructuredLogEntry {
    title: string
    meta: string[]
    detail?: string
    timingBar?: TimingWaterfallEntry | null
}

interface HandlerSummaryEntry {
    timestamp: string
    tool: string
    duration: number | null
    requestSummary: string | null
    responseSummary: string | null
    status: string
}

function formatHandlerSummaries(content: string): HandlerSummaryEntry[] {
    return content
        .split(/\r?\n/)
        .map(line => line.trim())
        .filter(line => line.length > 0)
        .map(line => {
            const record = safeParseJson(line)
            if (!record) {
                return null
            }

            const summary = pickJsonObject(record, ['summary'])
            if (!isHandlerSummaryRecord(record, summary)) {
                return null
            }

            const tool = pickString(record, ['handler'])
                ?? pickString(summary, ['handler'])
                ?? pickString(record, ['tool', 'name', 'method'])
                ?? pickString(summary, ['tool', 'name'])
                ?? 'unknown'
            const timestamp = pickString(record, ['timestamp', 'startedAt']) ?? ''
            const durationMs = pickNumber(record, ['durationMs', 'elapsedMs', 'latencyMs'])
            const status = pickString(record, ['status']) ?? 'unknown'
            const requestSummary = formatHandlerSummaryPayload(
                'request',
                pickJsonObject(record, ['request']) ?? pickJsonObject(summary, ['request']),
            )
                ?? pickString(record, ['requestSummary', 'request_summary'])
                ?? null
            const responseSummary = formatHandlerSummaryPayload(
                'response',
                pickJsonObject(record, ['response']) ?? pickJsonObject(summary, ['response']),
            )
                ?? pickString(record, ['responseSummary', 'response_summary'])
                ?? null

            return {
                timestamp,
                tool,
                duration: typeof durationMs === 'number' ? durationMs : null,
                requestSummary: requestSummary ?? null,
                responseSummary: responseSummary ?? null,
                status,
            } as HandlerSummaryEntry
        })
        .filter((entry): entry is HandlerSummaryEntry => entry !== null)
}

function isHandlerSummaryRecord(record: JsonObject, summary: JsonObject | null): boolean {
    if (pickString(record, ['handler']) ?? pickString(summary, ['handler'])) {
        return true
    }

    const tool = pickString(record, ['tool', 'name', 'method'])
        ?? pickString(summary, ['tool', 'name'])
    return tool === 'secureblob_component_call_handler'
}

function safeParseJson(text: string | null): JsonObject | null {
    if (typeof text !== 'string' || text.trim().length === 0) return null
    try {
        const value = JSON.parse(text) as unknown
        if (value && typeof value === 'object' && !Array.isArray(value)) {
            return value as JsonObject
        }
    } catch {
        return null
    }
    return null
}

function pickString(record: JsonObject | null, keys: readonly string[]): string | undefined {
    if (!record) return undefined
    for (const key of keys) {
        const value = record[key]
        if (typeof value === 'string' && value.trim().length > 0) {
            return value.trim()
        }
    }
    return undefined
}

function pickTimestamp(record: JsonObject | null, keys: readonly string[]): number | undefined {
    if (!record) return undefined
    for (const key of keys) {
        const value = record[key]
        if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
            return value
        }
        if (typeof value === 'string' && value.trim().length > 0) {
            const epoch = Date.parse(value)
            if (Number.isFinite(epoch) && epoch > 0) {
                return epoch
            }
        }
    }
    return undefined
}

function pickNumber(record: JsonObject | null, keys: readonly string[]): number | undefined {
    if (!record) return undefined
    for (const key of keys) {
        const value = record[key]
        if (typeof value === 'number' && Number.isFinite(value)) {
            return value
        }
        if (typeof value === 'string' && value.trim().length > 0) {
            const parsed = Number(value)
            if (Number.isFinite(parsed)) {
                return parsed
            }
        }
    }
    return undefined
}

function pickJsonObject(record: JsonObject | null, keys: readonly string[]): JsonObject | null {
    if (!record) return null
    for (const key of keys) {
        const value = record[key]
        if (value && typeof value === 'object' && !Array.isArray(value)) {
            return value as JsonObject
        }
    }
    return null
}

function isJsonObject(value: unknown): value is JsonObject {
    return Boolean(value) && typeof value === 'object' && !Array.isArray(value)
}

function isLikelyHandlerSourcePath(sourcePath: string): boolean {
    const fileName = sourcePath.split('/').pop()?.toLowerCase() ?? ''
    return fileName.includes('handler')
}

function validateHandlerInputDraft(draft: string): {
    normalizedInputJson: string | null
    error: string | null
} {
    const trimmed = draft.trim()
    if (trimmed.length === 0) {
        return {
            normalizedInputJson: null,
            error: null,
        }
    }

    const inputBytes = UTF8_ENCODER.encode(draft).byteLength
    if (inputBytes > HANDLER_INPUT_MAX_BYTES) {
        return {
            normalizedInputJson: null,
            error: `Handler input must be 1 MB or smaller; received ${inputBytes} bytes.`,
        }
    }

    try {
        JSON.parse(draft)
        return {
            normalizedInputJson: draft,
            error: null,
        }
    } catch (error) {
        return {
            normalizedInputJson: null,
            error: `Handler input must be valid JSON: ${error instanceof Error ? error.message : 'Unknown parse error'}`,
        }
    }
}

function summarizeHandlerInput(inputJson: string | null): string {
    if (!inputJson || inputJson.trim().length === 0) {
        return 'no input'
    }

    try {
        const parsed = JSON.parse(inputJson) as unknown
        if (Array.isArray(parsed)) {
            return `array(${parsed.length})`
        }
        if (parsed && typeof parsed === 'object') {
            const keys = Object.keys(parsed as Record<string, unknown>).filter(key => key.length > 0)
            return keys.length > 0
                ? keys.slice(0, 3).join(', ')
                : 'empty object'
        }
        return truncateInlineJson(JSON.stringify(parsed))
    } catch {
        return 'JSON input'
    }
}

function truncateInlineJson(value: string, maxLength: number = 72): string {
    return value.length > maxLength
        ? `${value.slice(0, maxLength)}...`
        : value
}

function createHandlerInvocationHistoryItem(
    result: ComponentHandlerCallResult,
    inputJson: string | null,
    startedAt: number,
): HandlerInvocationHistoryItem {
    return {
        id: `${startedAt}:${result.runId ?? result.statusCode}`,
        startedAt,
        statusCode: result.statusCode,
        durationMs: result.durationMs,
        inputJson,
        inputSummary: summarizeHandlerInput(inputJson),
        ...(result.runId ? { runId: result.runId } : {}),
        ...(typeof result.output !== 'undefined' ? { output: result.output } : {}),
        ...(result.message ? { message: result.message } : {}),
        ...(result.errorCode ? { errorCode: result.errorCode } : {}),
    }
}

function formatHandlerInvocationSummary(invocation: HandlerInvocationHistoryItem): string {
    switch (invocation.statusCode) {
        case 200:
            return 'Structured JSON output returned.'
        case 204:
            return 'Handler completed without JSON output.'
        case 401:
            return invocation.message ?? 'Check workspace access permissions.'
        case 408:
            return invocation.message ?? 'Handler timed out. Retry or inspect the run logs.'
        case 422:
            return invocation.message ?? 'Handler reported an execution error.'
        case 499:
            return invocation.message ?? 'Handler invocation was cancelled.'
        default:
            return invocation.message ?? `Handler returned HTTP ${invocation.statusCode}.`
    }
}

function isTranscriptPhase(value: unknown): value is 'commentary' | 'final_answer' {
    return value === 'commentary' || value === 'final_answer'
}

function normalizeTranscriptRefs(value: unknown): ResolvedRef[] {
    if (!Array.isArray(value)) return []

    const refs: ResolvedRef[] = []
    for (const entry of value) {
        if (!isJsonObject(entry)) continue
        const path = pickString(entry, ['path'])
        if (!path) continue

        const content = typeof entry.content === 'string'
            ? entry.content
            : null
        const size = pickNumber(entry, ['size'])
        const lineStart = pickNumber(entry, ['lineStart'])
        const lineEnd = pickNumber(entry, ['lineEnd'])
        const binaryBase64 = pickString(entry, ['binaryBase64'])
        const mime = pickString(entry, ['mime'])
        const isImage = entry.isImage === true

        const ref: ResolvedRef = {
            path,
            content,
            ...(typeof size === 'number' ? { size } : {}),
            ...(typeof lineStart === 'number' ? { lineStart } : {}),
            ...(typeof lineEnd === 'number' ? { lineEnd } : {}),
            ...(binaryBase64 ? { binaryBase64 } : {}),
            ...(mime ? { mime } : {}),
            ...(isImage ? { isImage: true } : {}),
        }
        refs.push(ref)
    }
    return refs
}

function normalizeTranscriptAttachments(value: unknown): Attachment[] {
    if (!Array.isArray(value)) return []

    return value
        .map(entry => {
            if (!isJsonObject(entry)) return null
            const path = pickString(entry, ['path'])
            const mime = pickString(entry, ['mime'])
            const name = pickString(entry, ['name'])
            const size = pickNumber(entry, ['size'])
            if (!path || !mime || !name || typeof size !== 'number') {
                return null
            }

            return { path, mime, name, size } satisfies Attachment
        })
        .filter((entry): entry is Attachment => entry !== null)
}

function normalizeTranscriptFileModifications(value: unknown): FileModification[] {
    if (!Array.isArray(value)) return []

    return value
        .map(entry => {
            if (!isJsonObject(entry)) return null
            const path = pickString(entry, ['path'])
            const action = pickString(entry, ['action'])
            if (!path || (action !== 'created' && action !== 'updated' && action !== 'deleted')) {
                return null
            }
            return { path, action } satisfies FileModification
        })
        .filter((entry): entry is FileModification => entry !== null)
}

function normalizeTranscriptEvent(value: unknown): TranscriptEvent | null {
    if (!isJsonObject(value)) return null

    const type = pickString(value, ['type'])
    const timestamp = pickNumber(value, ['timestamp'])
    if (!type || typeof timestamp !== 'number') {
        return null
    }

    switch (type) {
        case 'user_message':
            return {
                type,
                text: typeof value.text === 'string' ? value.text : '',
                refs: normalizeTranscriptRefs(value.refs),
                attachments: normalizeTranscriptAttachments(value.attachments),
                timestamp,
            }
        case 'assistant_thinking':
            return {
                type,
                ...(typeof value.text === 'string' ? { text: value.text } : {}),
                timestamp,
            }
        case 'assistant_stream_delta':
            return {
                type,
                text: typeof value.text === 'string' ? value.text : '',
                timestamp,
            }
        case 'assistant_message_final':
            return {
                type,
                text: typeof value.text === 'string' ? value.text : '',
                ...(isTranscriptPhase(value.phase) ? { phase: value.phase } : {}),
                timestamp,
            }
        case 'assistant_tool_batch':
            return {
                type,
                items: Array.isArray(value.items)
                    ? value.items
                        .map(entry => {
                            if (!isJsonObject(entry)) return null
                            const id = pickString(entry, ['id'])
                            const name = pickString(entry, ['name'])
                            if (!id || !name) return null
                            const args = isJsonObject(entry.args) ? entry.args : {}
                            const responseId = pickString(entry, ['responseId'])
                            const status = pickString(entry, ['status'])
                            return {
                                id,
                                name,
                                args,
                                ...(responseId ? { responseId } : {}),
                                ...(status ? { status } : {}),
                            }
                        })
                        .filter((entry): entry is NonNullable<typeof entry> => entry !== null)
                    : [],
                timestamp,
            }
        case 'assistant_reasoning_item':
            return {
                type,
                id: pickString(value, ['id']) ?? `reasoning-${timestamp}`,
                ...(pickString(value, ['encryptedContent']) ? { encryptedContent: pickString(value, ['encryptedContent']) } : {}),
                ...(Array.isArray(value.summary)
                    ? {
                        summary: value.summary.filter((item): item is string => typeof item === 'string' && item.length > 0),
                    }
                    : {}),
                ...(pickString(value, ['status']) ? { status: pickString(value, ['status']) } : {}),
                timestamp,
            }
        case 'assistant_image': {
            const dataUrl = pickString(value, ['dataUrl'])
            const savedPath = pickString(value, ['savedPath'])
            if (!dataUrl || !savedPath) {
                return null
            }
            return {
                type,
                dataUrl,
                savedPath,
                ...(pickString(value, ['promotedPath']) ? { promotedPath: pickString(value, ['promotedPath']) } : {}),
                timestamp,
            }
        }
        case 'tool_call': {
            const id = pickString(value, ['id'])
            const name = pickString(value, ['name'])
            if (!id || !name) {
                return null
            }
            const status = pickString(value, ['status'])
            return {
                type,
                id,
                name,
                args: isJsonObject(value.args) ? value.args : {},
                status: status === 'pending' || status === 'running' || status === 'completed' || status === 'error'
                    ? status
                    : 'completed',
                timestamp,
            }
        }
        case 'tool_result': {
            const id = pickString(value, ['id'])
            const name = pickString(value, ['name'])
            if (!id || !name) {
                return null
            }
            const result = typeof value.result === 'string'
                ? value.result
                : JSON.stringify(value.result ?? null, null, 2)
            const filesModified = normalizeTranscriptFileModifications(value.filesModified)
            return {
                type,
                id,
                name,
                result,
                ...(filesModified.length > 0 ? { filesModified } : {}),
                timestamp,
            }
        }
        case 'error':
            return {
                type,
                message: typeof value.message === 'string' ? value.message : 'Unknown error',
                retryable: value.retryable === true,
                timestamp,
            }
        case 'cost_update':
            return {
                type,
                tokensIn: pickNumber(value, ['tokensIn']) ?? 0,
                tokensOut: pickNumber(value, ['tokensOut']) ?? 0,
                estimatedCostUsd: pickNumber(value, ['estimatedCostUsd']) ?? 0,
                timestamp,
            }
        default:
            return null
    }
}

function collectTranscriptFilesModified(events: readonly TranscriptEvent[]): FileModification[] {
    const deduped = new Map<string, FileModification>()

    for (const event of events) {
        if (event.type !== 'tool_result' || !event.filesModified) continue
        for (const modification of event.filesModified) {
            deduped.set(`${modification.action}:${modification.path}`, modification)
        }
    }

    return [...deduped.values()]
}

function countTranscriptAttachments(events: readonly TranscriptEvent[]): number {
    return events.reduce((count, event) => (
        event.type === 'user_message'
            ? count + (event.attachments?.length ?? 0)
            : count
    ), 0)
}

function parseRunTranscript(text: string): { transcript: RunTranscriptRecord | null; error: string | null } {
    const parsed = safeParseJson(text)
    if (!parsed) {
        return {
            transcript: null,
            error: 'Transcript bundle is not valid JSON.',
        }
    }

    if (pickString(parsed, ['schema']) !== 'chat-transcript/v2') {
        return {
            transcript: null,
            error: 'Unsupported transcript schema.',
        }
    }

    const uiEvents = Array.isArray(parsed.uiEvents)
        ? parsed.uiEvents
            .map(normalizeTranscriptEvent)
            .filter((event): event is TranscriptEvent => event !== null)
        : []
    const filesModified = collectTranscriptFilesModified(uiEvents)
    const config = pickJsonObject(parsed, ['config'])
    const provider = pickJsonObject(config, ['provider'])
    const cost = pickJsonObject(parsed, ['cost'])

    return {
        transcript: {
            schema: 'chat-transcript/v2',
            sessionId: pickString(parsed, ['sessionId']) ?? 'unknown-session',
            status: pickString(parsed, ['status']) ?? 'unknown',
            agentPath: pickString(parsed, ['agentPath']) ?? null,
            agentConfigPath: pickString(parsed, ['agentConfigPath']) ?? null,
            agentName: pickString(config, ['name']) ?? null,
            model: pickString(config, ['model']) ?? null,
            providerType: pickString(provider, ['type']) ?? null,
            providerApiMode: pickString(provider, ['apiMode']) ?? null,
            updatedAt: pickNumber(parsed, ['updatedAt']) ?? null,
            cost: cost
                ? {
                    tokensIn: pickNumber(cost, ['tokensIn']) ?? 0,
                    tokensOut: pickNumber(cost, ['tokensOut']) ?? 0,
                    estimatedCostUsd: pickNumber(cost, ['estimatedCostUsd']) ?? 0,
                }
                : null,
            runId: pickString(parsed, ['runId']) ?? null,
            sourcePath: pickString(parsed, ['sourcePath']) ?? null,
            artifactId: pickString(parsed, ['artifactId']) ?? null,
            attachmentCount: countTranscriptAttachments(uiEvents),
            filesModified,
            uiEvents,
        },
        error: null,
    }
}

function parseRunTranscriptSession(path: string): { sessionId: string; relativePath: string } | null {
    const parsed = parseComponentJobPath(path)
    if (parsed?.kind !== 'run_output' || !parsed.relativePath) {
        return null
    }

    const match = parsed.relativePath.match(/^agent-sessions\/([^/]+)\/transcript\.json$/)
    if (!match) {
        return null
    }

    return {
        sessionId: match[1],
        relativePath: parsed.relativePath,
    }
}

function isRunProbeOutputPath(path: string): boolean {
    const parsed = parseComponentJobPath(path)
    return parsed?.kind === 'run_output'
        && typeof parsed.relativePath === 'string'
        && parsed.relativePath.startsWith('probe/')
        && parsed.relativePath.endsWith('.json')
}

function parseRunProbe(text: string): { probe: ComponentJobRunProbe | null; error: string | null } {
    const parsed = safeParseJson(text)
    if (!parsed) {
        return {
            probe: null,
            error: 'Probe bundle is not valid JSON.',
        }
    }

    if (pickString(parsed, ['schema']) !== 'secureblob.component-job-run-probe/v1') {
        return {
            probe: null,
            error: 'Unsupported probe bundle schema.',
        }
    }

    const runtimeSnapshot = pickJsonObject(parsed, ['runtimeSnapshot'])
    if (!runtimeSnapshot) {
        return {
            probe: null,
            error: 'Probe bundle is missing runtimeSnapshot.',
        }
    }

    const screenshot = (() => {
        const candidate = parsed.screenshot as unknown
        if (!candidate || typeof candidate !== 'object') {
            return null
        }

        const data = (candidate as Record<string, unknown>).dataBase64
        const contentType = (candidate as Record<string, unknown>).contentType
        if (contentType !== 'image/png' || typeof data !== 'string' || data.trim().length === 0) {
            return null
        }

        return {
            contentType: 'image/png',
            dataBase64: data.trim(),
        } as ComponentJobRunProbe['screenshot']
    })()

    const screenshotRef = pickString(parsed, ['screenshotRef']) ?? null

    const runtimeTrace = Array.isArray(parsed.runtimeTrace)
        ? parsed.runtimeTrace.filter((entry): entry is Record<string, unknown> => typeof entry === 'object' && entry !== null)
        : []
    const consoleErrors = Array.isArray(parsed.consoleErrors)
        ? parsed.consoleErrors.filter((entry): entry is string => typeof entry === 'string')
        : []
    const networkFailures = Array.isArray(parsed.networkFailures)
        ? parsed.networkFailures.filter((entry): entry is Record<string, unknown> => typeof entry === 'object' && entry !== null)
            .map(entry => ({
                url: pickString(entry, ['url']) ?? '(unknown)',
                errorText: pickString(entry, ['errorText']) ?? '(unknown)',
                ...(pickString(entry, ['method']) ? { method: pickString(entry, ['method']) ?? undefined } : {}),
                ...(pickString(entry, ['resourceType']) ? { resourceType: pickString(entry, ['resourceType']) ?? undefined } : {}),
            }))
        : []
    const httpErrors = Array.isArray(parsed.httpErrors)
        ? parsed.httpErrors.filter((entry): entry is Record<string, unknown> => typeof entry === 'object' && entry !== null)
            .map(entry => ({
                url: pickString(entry, ['url']) ?? '(unknown)',
                status: pickNumber(entry, ['status']) ?? 0,
                ...(pickString(entry, ['method']) ? { method: pickString(entry, ['method']) ?? undefined } : {}),
                ...(pickString(entry, ['statusText']) ? { statusText: pickString(entry, ['statusText']) ?? undefined } : {}),
                ...(pickString(entry, ['resourceType']) ? { resourceType: pickString(entry, ['resourceType']) ?? undefined } : {}),
                ...(pickString(entry, ['capturedAt']) ? { capturedAt: pickString(entry, ['capturedAt']) ?? undefined } : {}),
            }))
            .filter(entry => entry.status > 0)
        : []

    // §258: Parse optional sqliteLaneState array from probe bundle.
    const sqliteLaneState = Array.isArray(parsed.sqliteLaneState)
        ? parsed.sqliteLaneState
            .filter((entry): entry is Record<string, unknown> => typeof entry === 'object' && entry !== null)
            .map(entry => ({
                lane: pickString(entry, ['lane']) ?? 'unknown',
                pageCount: pickNumber(entry, ['pageCount']) ?? 0,
                dirtyPageCount: pickNumber(entry, ['dirtyPageCount']) ?? 0,
                transactionMode: pickString(entry, ['transactionMode']) ?? 'unknown',
                lastCommitAt: pickString(entry, ['lastCommitAt']) ?? null,
            }))
        : []

    return {
        probe: {
            schema: 'secureblob.component-job-run-probe/v1',
            capturedAt: pickString(parsed, ['capturedAt']) ?? '',
            sourcePath: pickString(parsed, ['sourcePath']) ?? '',
            runId: pickString(parsed, ['runId']) ?? null,
            buildId: pickString(parsed, ['buildId']) ?? null,
            artifactId: pickString(parsed, ['artifactId']) ?? null,
            status: pickString(parsed, ['status']) ?? 'failed',
            stage: pickString(parsed, ['stage']) ?? 'failed',
            message: pickString(parsed, ['message']) ?? null,
            error: pickString(parsed, ['error']) ?? null,
            screenshotRef,
            screenshot,
            consoleErrors,
            networkFailures,
            httpErrors,
            runtimeSnapshot: runtimeSnapshot as unknown as ComponentJobRunProbe['runtimeSnapshot'],
            runtimeTrace: runtimeTrace as unknown as ComponentJobRunProbe['runtimeTrace'],
            sqliteLaneState,
        },
        error: null,
    }
}

function formatHandlerSummaryPayload(prefix: string, payload: JsonObject | null): string | undefined {
    if (!payload) return undefined

    // Structured fields from nested summary.request / summary.response objects
    // (ComponentHandlerService writes these into mcp.jsonl as nested objects)
    const method = pickString(payload, ['method', 'name', 'tool', 'operation'])
    const status = pickString(payload, ['status'])
    const requestId = pickString(payload, ['requestId', 'callId', 'id'])
    const path = pickString(payload, ['path', 'uri', 'url'])
    const query = pickString(payload, ['query'])
    const count = pickNumber(payload, ['count', 'items'])
    const summary = pickString(payload, ['summary', 'result', 'message'])

    const methodOrPath = method ?? path
    const detail = [
        methodOrPath,
        status,
        query,
        requestId,
        typeof count === 'number' ? `${count} items` : undefined,
        summary,
    ].filter((value): value is string => typeof value === 'string' && value.length > 0)

    if (detail.length > 0) {
        return `${prefix}: ${detail.join(' · ')}`
    }

    const compact = compactJsonRecord(payload, ['method', 'name', 'tool', 'operation', 'status', 'query', 'path', 'uri', 'url', 'summary', 'requestId', 'callId', 'id', 'count', 'items'])
    return compact ? `${prefix}: ${compact}` : undefined
}

function isRunLogTab(tab: RunInspectorTabId): tab is RunLogTabId {
    return tab in RUN_LOG_TAB_TO_NAME
}

function isStructuredRunLogTab(tab: RunInspectorTabId): tab is Extract<RunLogTabId, 'events' | 'http' | 'mcp'> {
    return tab === 'events' || tab === 'http' || tab === 'mcp'
}

function getRunLogNameForTab(tab: RunLogTabId): RunLogName {
    return RUN_LOG_TAB_TO_NAME[tab]
}

function getRunLogPathForName(
    runPaths: ReturnType<typeof getComponentRunPaths>,
    logName: RunLogName,
): string {
    switch (logName) {
        case 'stdout.log':
            return runPaths.stdout
        case 'stderr.log':
            return runPaths.stderr
        case 'events.jsonl':
            return runPaths.events
        case 'http.jsonl':
            return runPaths.http
        case 'mcp.jsonl':
            return runPaths.mcp
        default:
            return runPaths.stdout
    }
}

function appendLogLine(current: string | null, line: string): string {
    const normalizedLine = line.replace(/\r$/, '')
    if (!current || current.length === 0) {
        return `${normalizedLine}\n`
    }
    return `${current}${current.endsWith('\n') ? '' : '\n'}${normalizedLine}\n`
}

function compactJsonRecord(record: JsonObject, omittedKeys: readonly string[]): string | undefined {
    const compactEntries = Object.entries(record).filter(([key, value]) => {
        if (omittedKeys.includes(key)) return false
        if (value === null || value === undefined) return false
        if (typeof value === 'string' && value.trim().length === 0) return false
        return true
    })
    if (compactEntries.length === 0) return undefined
    return JSON.stringify(Object.fromEntries(compactEntries))
}

function formatRuntimeTraceMeta(entry: ComponentJobRunProbe['runtimeTrace'][number]): string {
    if (entry.kind === 'sqlite-write-lane') {
        const data = entry.data as JsonObject
        const lane = pickString(data, ['lane'])
        const mode = pickString(data, ['transactionMode'])
        const changedPageCount = pickNumber(data, ['changedPageCount'])
        const pageCount = pickNumber(data, ['pageCount'])
        const durationMs = pickNumber(data, ['durationMs'])
        const path = pickString(data, ['path'])

        const pageSummary = typeof changedPageCount === 'number' && typeof pageCount === 'number'
            ? `${changedPageCount}/${pageCount} pages`
            : typeof changedPageCount === 'number'
                ? `${changedPageCount} pages`
                : 'pages'

        return [
            'sqlite-write-lane',
            lane ? `lane:${lane}` : null,
            path ? `path:${path}` : null,
            mode ? `mode:${mode}` : null,
            pageSummary,
            typeof durationMs === 'number' ? `${durationMs}ms` : null,
            entry.timestamp,
        ].filter((value): value is string => typeof value === 'string' && value.length > 0).join(' · ')
    }

    if (entry.kind === 'sqlite-lane-state') {
        const data = entry.data as JsonObject
        const lane = pickString(data, ['lane'])
        const pageCount = pickNumber(data, ['pageCount'])
        const dirtyPageCount = pickNumber(data, ['dirtyPageCount'])
        const mode = pickString(data, ['transactionMode'])

        return [
            'sqlite-lane-state',
            lane ? `lane:${lane}` : null,
            typeof pageCount === 'number' ? `${pageCount} pages` : null,
            typeof dirtyPageCount === 'number' ? `${dirtyPageCount} dirty` : null,
            mode && mode !== 'unknown' ? `mode:${mode}` : null,
            entry.timestamp,
        ].filter((value): value is string => typeof value === 'string' && value.length > 0).join(' · ')
    }

    return [
        entry.kind,
        entry.source,
        entry.timestamp,
    ].filter((value): value is string => typeof value === 'string' && value.length > 0).join(' · ')
}

function formatRuntimeTraceTitle(entry: ComponentJobRunProbe['runtimeTrace'][number]): string {
    if (entry.kind === 'sqlite-write-lane') {
        return 'SQLite write lane'
    }
    if (entry.kind === 'sqlite-lane-state') {
        return 'SQLite lane state'
    }
    return 'Trace'
}

function formatRuntimeTraceDetail(entry: ComponentJobRunProbe['runtimeTrace'][number]): string | undefined {
    if (entry.kind === 'sqlite-write-lane') {
        const data = entry.data as JsonObject
        const lane = pickString(data, ['lane'])
        const mode = pickString(data, ['transactionMode'])
        const changedPageCount = pickNumber(data, ['changedPageCount'])
        const pageCount = pickNumber(data, ['pageCount'])
        const path = pickString(data, ['path']) ?? 'unknown path'
        const changed = data.changed === true ? 'changed' : 'unchanged'
        const rowsAffected = pickNumber(data, ['rowsAffected'])
        const lastInsertRowid = pickNumber(data, ['lastInsertRowid'])

        const pages = typeof changedPageCount === 'number' && typeof pageCount === 'number'
            ? `${changedPageCount}/${pageCount}`
            : typeof changedPageCount === 'number'
                ? `${changedPageCount}`
                : 'unknown'

        const rows = typeof rowsAffected === 'number'
            ? `rowsAffected=${rowsAffected}`
            : typeof lastInsertRowid === 'number'
                ? `lastInsertRowid=${lastInsertRowid}`
                : null

        return [
            `SQLite write: ${pages} pages (${mode ?? 'unknown'}) on lane ${lane ?? 'unknown'}`,
            `path=${path}`,
            changed,
            rows,
        ].filter((entry): entry is string => entry !== null).join(' · ')
    }

    if (entry.kind === 'sqlite-lane-state') {
        const data = entry.data as JsonObject
        const lane = pickString(data, ['lane'])
        const pageCount = pickNumber(data, ['pageCount'])
        const dirtyPageCount = pickNumber(data, ['dirtyPageCount'])
        const mode = pickString(data, ['transactionMode'])
        const lastCommitAt = pickString(data, ['lastCommitAt'])

        return [
            `lane ${lane ?? '?'}: ${typeof pageCount === 'number' ? pageCount : '?'} pages`,
            typeof dirtyPageCount === 'number' ? `${dirtyPageCount} dirty` : null,
            mode && mode !== 'unknown' ? `mode: ${mode}` : null,
            lastCommitAt ? `last commit: ${lastCommitAt}` : null,
        ].filter((v): v is string => v !== null).join(' · ')
    }

    return compactJsonRecord(entry.data as JsonObject, [])
}

function getProbeImageSrc(screenshot: ComponentJobRunProbe['screenshot']): string | null {
    if (!screenshot) {
        return null
    }

    return `data:${screenshot.contentType};base64,${screenshot.dataBase64}`
}

function renderComponentJobProbePanel(
    probe: ComponentJobRunProbe | null,
    parseError: string | null,
    rawContent: string,
    fallbackTitle: string,
) {
    if (!probe) {
        return (
            <div className="component-job-transcript-panel">
                {parseError && (
                    <p className="component-job-form-error">{parseError}</p>
                )}
                <pre className="component-job-log-preview">{rawContent}</pre>
            </div>
        )
    }

    const runtimeSnapshot = probe.runtimeSnapshot
    const metaItems = [
        formatStatusLabel(probe.status),
        formatStatusLabel(probe.stage),
        probe.consoleErrors.length > 0 ? `${probe.consoleErrors.length} console error${probe.consoleErrors.length === 1 ? '' : 's'}` : null,
        probe.networkFailures.length > 0 ? `${probe.networkFailures.length} network failure${probe.networkFailures.length === 1 ? '' : 's'}` : null,
        probe.httpErrors.length > 0 ? `${probe.httpErrors.length} HTTP error${probe.httpErrors.length === 1 ? '' : 's'}` : null,
        probe.capturedAt || null,
    ].filter((value): value is string => typeof value === 'string' && value.length > 0)
    const runtimeMeta = [
        runtimeSnapshot.uxState,
        runtimeSnapshot.activeWorkspace?.blobId ?? runtimeSnapshot.workspaceReadiness.blobId ?? null,
        runtimeSnapshot.online ? 'online' : 'offline',
        runtimeSnapshot.localServerMode ? 'local-server' : 'browser',
        runtimeSnapshot.mcpToolCalls.length > 0 ? `${runtimeSnapshot.mcpToolCalls.length} MCP call${runtimeSnapshot.mcpToolCalls.length === 1 ? '' : 's'}` : null,
        probe.runtimeTrace.length > 0 ? `${probe.runtimeTrace.length} trace event${probe.runtimeTrace.length === 1 ? '' : 's'}` : null,
    ].filter((value): value is string => typeof value === 'string' && value.length > 0)

    return (
        <div className="component-job-transcript-panel">
            <div className="component-job-transcript-meta" role="list" aria-label="Browser probe metadata">
                {metaItems.map((item, index) => (
                    <span key={`${index}:${item}`} role="listitem">{item}</span>
                ))}
            </div>
            {(probe.message || probe.error) && (
                <div className="component-job-jsonlog-list" role="list">
                    <article className="component-job-jsonlog-item" role="listitem">
                        <strong>{probe.message ?? fallbackTitle}</strong>
                        {probe.error && <code>{probe.error}</code>}
                    </article>
                </div>
            )}
            <div className="component-job-jsonlog-list" role="list">
                <article className="component-job-jsonlog-item" role="listitem">
                    <strong>Runtime snapshot</strong>
                    {runtimeMeta.length > 0 && <span>{runtimeMeta.join(' · ')}</span>}
                    <code>{runtimeSnapshot.url}</code>
                </article>
                {probe.screenshot && (() => {
                    const src = getProbeImageSrc(probe.screenshot)
                    if (!src) return null

                    return (
                        <article key="screenshot" className="component-job-jsonlog-item" role="listitem">
                            <strong>Screenshot</strong>
                            <img
                                src={src}
                                alt="Probe screenshot"
                                style={{
                                    maxWidth: '100%',
                                    border: '1px solid var(--color-border, #333)',
                                    borderRadius: '8px',
                                }}
                            />
                        </article>
                    )
                })()}
                {probe.screenshotRef && !probe.screenshot && (
                    <article key="screenshot-ref" className="component-job-jsonlog-item" role="listitem">
                        <strong>Screenshot</strong>
                        <code>{probe.screenshotRef}</code>
                    </article>
                )}
                {probe.consoleErrors.map((entry, index) => (
                    <article key={`console-${index}`} className="component-job-jsonlog-item" role="listitem">
                        <strong>Console</strong>
                        <code>{entry}</code>
                    </article>
                ))}
                {probe.networkFailures.map((entry, index) => (
                    <article key={`network-${index}`} className="component-job-jsonlog-item" role="listitem">
                        <strong>Network</strong>
                        <span>{[entry.method ?? 'GET', entry.url].join(' ')}</span>
                        <code>{entry.errorText}</code>
                    </article>
                ))}
                {probe.httpErrors.map((entry, index) => (
                    <article key={`http-${index}`} className="component-job-jsonlog-item" role="listitem">
                        <strong>HTTP</strong>
                        <span>{[(entry.method ?? 'GET'), entry.url].join(' ')}</span>
                        <code>{`status ${entry.status}`}</code>
                    </article>
                ))}
                {/** §258: SQLite Lane State panel — shows current lane head, dirty page count,
                 * transaction mode, and last commit timestamp for each job-attached sqlite3mt
                 * session. Rendered above the runtime trace entries. */}
                {probe.sqliteLaneState.length > 0 && (
                    <details className="component-job-sqlite-lane-state" open>
                        <summary className="component-job-jsonlog-item">
                            <strong>SQLite Lane</strong>
                            <span>{probe.sqliteLaneState.length} lane{probe.sqliteLaneState.length === 1 ? '' : 's'}</span>
                        </summary>
                        {probe.sqliteLaneState.map((lane, index) => (
                            <article key={`lane-${index}`} className="component-job-jsonlog-item component-job-sqlite-lane-state-entry" role="listitem">
                                <strong>{lane.lane}</strong>
                                <span>
                                    {[
                                        typeof lane.pageCount === 'number' ? `${lane.pageCount} pages` : null,
                                        lane.dirtyPageCount > 0 ? `${lane.dirtyPageCount} dirty` : null,
                                        lane.transactionMode && lane.transactionMode !== 'unknown' ? lane.transactionMode : null,
                                        lane.lastCommitAt ? `committed ${lane.lastCommitAt}` : null,
                                    ].filter((v): v is string => v !== null).join(' · ')}
                                </span>
                            </article>
                        ))}
                    </details>
                )}
                {probe.runtimeTrace.map((entry, index) => {
                    const meta = formatRuntimeTraceMeta(entry)
                    const title = formatRuntimeTraceTitle(entry)
                    const detailLine = formatRuntimeTraceDetail(entry)
                    return (
                        <article key={`trace-${index}`} className="component-job-jsonlog-item" role="listitem">
                            <strong>{title}</strong>
                            {meta && <span>{meta}</span>}
                            {detailLine && <code>{detailLine}</code>}
                        </article>
                    )
                })}
            </div>
        </div>
    )
}

function formatStructuredLogEntries(tab: Extract<RunLogTabId, 'events' | 'http' | 'mcp'>, content: string): StructuredLogEntry[] {
    return content
        .split(/\r?\n/)
        .map(line => line.trim())
        .filter(line => line.length > 0)
        .map(line => {
            const record = safeParseJson(line)
            if (!record) {
                return { title: line, meta: [] }
            }

            switch (tab) {
                case 'events': {
                    const stage = pickString(record, ['stage', 'status'])
                    const type = pickString(record, ['type'])
                    const message = pickString(record, ['message', 'summary'])
                    const timestamp = pickString(record, ['timestamp', 'updatedAt'])
                    const elapsed = pickNumber(record, ['totalElapsedSeconds'])
                    const source = pickString(record, ['source'])
                    const title = [stage ? formatStatusLabel(stage) : null, message ?? type ?? 'Event']
                        .filter(Boolean)
                        .join(' · ')

                    return {
                        title,
                        meta: [
                            timestamp,
                            typeof elapsed === 'number' ? `${elapsed}s elapsed` : undefined,
                            source,
                        ].filter((value): value is string => typeof value === 'string' && value.length > 0),
                        detail: pickString(record, ['error']) ?? compactJsonRecord(
                            record,
                            ['stage', 'status', 'type', 'message', 'summary', 'timestamp', 'updatedAt', 'totalElapsedSeconds', 'source', 'error'],
                        ),
                    }
                }
                case 'http': {
                    const method = pickString(record, ['method'])?.toUpperCase() ?? 'HTTP'
                    const target = pickString(record, ['url', 'target', 'uri', 'path'])
                    const status = pickString(record, ['status']) ?? (typeof pickNumber(record, ['status']) === 'number' ? String(pickNumber(record, ['status'])) : undefined)
                    const durationMs = pickNumber(record, ['durationMs', 'elapsedMs', 'latencyMs'])
                    const dnsMs = pickNumber(record, ['dnsMs'])
                    const connectMs = pickNumber(record, ['connectMs'])
                    const tlsMs = pickNumber(record, ['tlsMs'])
                    const ttfbMs = pickNumber(record, ['ttfbMs'])
                    const downloadMs = pickNumber(record, ['downloadMs'])
                    const title = target ? `${method} ${target}` : method

                    const timingBar: TimingWaterfallEntry | null =
                        (dnsMs != null || connectMs != null || tlsMs != null || ttfbMs != null || downloadMs != null)
                            ? {
                                dnsMs: dnsMs ?? null,
                                connectMs: connectMs ?? null,
                                tlsMs: tlsMs ?? null,
                                ttfbMs: ttfbMs ?? null,
                                downloadMs: downloadMs ?? null,
                                totalMs: typeof durationMs === 'number' ? durationMs : 0,
                            }
                            : null

                    return {
                        title,
                        meta: [
                            status ? `status ${status}` : undefined,
                            typeof durationMs === 'number' ? `${durationMs}ms` : undefined,
                        ].filter((value): value is string => typeof value === 'string' && value.length > 0),
                        detail: pickString(record, ['message', 'error']) ?? compactJsonRecord(
                            record,
                            ['method', 'url', 'target', 'uri', 'path', 'status', 'durationMs', 'elapsedMs', 'latencyMs', 'dnsMs', 'connectMs', 'tlsMs', 'ttfbMs', 'downloadMs', 'message', 'error'],
                        ),
                        timingBar,
                    }
                }
                case 'mcp': {
                    const server = pickString(record, ['server', 'client'])
                    const summary = pickJsonObject(record, ['summary'])
                    const tool =
                        pickString(record, ['tool', 'name', 'method'])
                        ?? pickString(summary, ['tool', 'name'])
                        ?? pickString(record, ['handler'])
                        ?? pickString(summary, ['handler'])
                    const status = pickString(record, ['status'])
                    const durationMs = pickNumber(record, ['durationMs', 'elapsedMs', 'latencyMs'])
                    const callId = pickString(record, ['requestId', 'callId'])
                    const requestSummary = formatHandlerSummaryPayload(
                        'request',
                        pickJsonObject(record, ['request']) ?? pickJsonObject(summary, ['request']),
                    )
                    const responseSummary = formatHandlerSummaryPayload(
                        'response',
                        pickJsonObject(record, ['response']) ?? pickJsonObject(summary, ['response']),
                    )
                    const title = [server, tool].filter(Boolean).join(' · ') || 'MCP call'
                    const detailParts = [
                        pickString(record, ['message']),
                        pickString(record, ['error']),
                        requestSummary,
                        responseSummary,
                    ].filter((value): value is string => typeof value === 'string' && value.length > 0)

                    return {
                        title,
                        meta: [
                            status,
                            typeof durationMs === 'number' ? `${durationMs}ms` : undefined,
                            callId,
                        ].filter((value): value is string => typeof value === 'string' && value.length > 0),
                        detail: detailParts.length > 0
                            ? detailParts.join(' · ')
                            : compactJsonRecord(
                            record,
                            ['server', 'client', 'tool', 'name', 'method', 'status', 'durationMs', 'elapsedMs', 'latencyMs', 'requestId', 'callId', 'message', 'error'],
                        ),
                    }
                }
                default:
                    return { title: line, meta: [] }
            }
        })
}

function isAbortError(error: unknown): boolean {
    return typeof error === 'object'
        && error !== null
        && 'name' in error
        && (error as { name?: string }).name === 'AbortError'
}

function selectAppIacMcpConnection(
    connections: readonly McpConnectionEntry[],
    workspaceBlobId: string,
): McpConnectionEntry | null {
    const purposePriority = (purpose: string | undefined): number => {
        switch (purpose) {
            case 'iac-master': return 0
            case 'server-runner': return 1
            case 'iac-child': return 2
            case 'user-mcp': return 3
            default: return 4
        }
    }

    return connections
        .filter(connection =>
            connection.workspaceBlobIds.includes(workspaceBlobId)
            && connection.configBlobId.trim().length > 0
            && connection.readKey.trim().length > 0
            && connection.writerSeed.trim().length > 0)
        .sort((left, right) => {
            const purposeDiff = purposePriority(left.purpose) - purposePriority(right.purpose)
            if (purposeDiff !== 0) return purposeDiff
            return left.label.localeCompare(right.label)
        })[0] ?? null
}

function describeAppIacRegistration(status: SecureBlobAppIacStatusResult | null): string {
    if (!status) return 'Not inspected'
    if (status.managedRegistrations.length === 0) return 'No server registry entry'
    const enabled = status.managedRegistrations.filter(registration => registration.enabled).length
    return `${enabled}/${status.managedRegistrations.length} enabled`
}

function describeAppIacInstall(status: SecureBlobAppIacStatusResult | null): string {
    const workspace = status?.workspaces[0]
    if (!workspace) return 'Not inspected'
    if (!workspace.appManifest.exists) return 'Workspace manifest missing'
    return workspace.healthy ? 'Installed and healthy' : 'Installed with issues'
}

function describeAppIacJob(status: SecureBlobAppIacStatusResult | null, jobRootPath: string | null): string {
    const workspace = status?.workspaces[0]
    if (!workspace) return 'Not inspected'
    const job = jobRootPath
        ? workspace.componentJobs.find(candidate => candidate.jobRoot === jobRootPath)
        : workspace.componentJobs[0]
    if (!job) return 'No manifest job match'
    if (job.healthy) return 'Job state healthy'
    if (!job.jobConfigExists || !job.scheduleExists) return 'Job files incomplete'
    if (job.sourceSnapshotMatches === false) return 'Source snapshot stale'
    if (job.enabled && !job.artifactId) return 'No promoted artifact'
    if (job.artifactId && !job.artifactStorePresent && !job.artifactRehydratableFromWorkspace) return 'Artifact not rehydratable'
    return 'Job needs attention'
}

function summarizeAppIacIssues(status: SecureBlobAppIacStatusResult | null, jobRootPath: string | null): string[] {
    if (!status) return []

    const workspace = status.workspaces[0]
    const job = jobRootPath
        ? workspace?.componentJobs.find(candidate => candidate.jobRoot === jobRootPath)
        : workspace?.componentJobs[0]

    return [
        ...status.errors,
        ...status.warnings,
        ...(workspace?.errors ?? []),
        ...(workspace?.warnings ?? []),
        ...(job?.errors ?? []),
        ...(job?.warnings ?? []),
        ...((job?.recentBuilds ?? [])
            .filter(build => build.stage === 'failed')
            .map(build => `Build ${shortBuildId(build.buildId)} failed: ${build.error || build.message || 'unknown error'}`)),
        ...((job?.recentRuns ?? [])
            .filter(run => run.stage === 'failed')
            .map(run => `Run ${run.runId.length > 8 ? run.runId.slice(0, 8) : run.runId} failed: ${run.error || run.message || 'unknown error'}`)),
        ...((job?.missingPaths ?? []).map(path => `Missing ${path}`)),
        ...((job?.statusCards ?? [])
            .filter(card => card.tone === 'warn' || card.tone === 'error')
            .map(card => `${card.title}: ${card.status}${card.detail ? ` · ${card.detail}` : ''}`)),
    ].filter((value, index, all) => value.length > 0 && all.indexOf(value) === index).slice(0, 8)
}

function appIacStatusCardToneClass(card: SecureBlobAppIacStatusCard): 'ok' | 'warn' | 'neutral' {
    if (card.tone === 'ok' || card.tone === 'info') return 'ok'
    if (card.tone === 'warn' || card.tone === 'error') return 'warn'
    return 'neutral'
}

function formatStatusLabel(status: string): string {
    return status
        .replace(/[_-]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
        .replace(/\b\w/g, match => match.toUpperCase())
}

function formatHistoryTimestamp(timestamp: number): string {
    const date = new Date(timestamp)
    return `${date.toLocaleDateString('sv')} ${date.toLocaleTimeString('sv', { hour: '2-digit', minute: '2-digit' })}`
}

function shortArtifactId(artifactId: string): string {
    return artifactId.length > 8 ? artifactId.slice(0, 8) : artifactId
}

function shortBuildId(buildId: string): string {
    return buildId.length > 8 ? buildId.slice(0, 8) : buildId
}

function formatArtifactBytes(size: number | undefined): string | null {
    if (typeof size !== 'number' || !Number.isFinite(size) || size < 0) return null
    if (size < 1024) return `${size} B`
    if (size < 1024 * 1024) return `${Math.round(size / 102.4) / 10} KB`
    return `${Math.round(size / 1024 / 102.4) / 10} MB`
}

function getBrowserTimeZone(): string {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
    } catch {
        return 'UTC'
    }
}

function formatTargetLabel(target: string): string {
    return target === 'local' ? 'Local' : 'Server'
}

function isNotFoundComponentJobsError(error: unknown): boolean {
    return typeof error === 'object'
        && error !== null
        && 'code' in error
        && (error as { code?: string }).code === 'NOT_FOUND'
}

function formatBuildStatusChip(
    liveBuild: ComponentBuildStatusRecord | null,
    activeBuild: BuildHistoryItem | null,
    latestBuildIsStale: boolean,
): string {
    if (liveBuild) {
        const artifact = liveBuild.artifactId ? `artifact ${shortArtifactId(liveBuild.artifactId)} · ` : ''
        return `${artifact}${formatStatusLabel(liveBuild.stage)}`
    }
    if (activeBuild) {
        return latestBuildIsStale
            ? `artifact ${shortArtifactId(activeBuild.artifactId)} · Stale`
            : `artifact ${shortArtifactId(activeBuild.artifactId)} · ${activeBuild.statusLabel}`
    }
    return 'No build yet'
}

function formatElapsedLabel(totalElapsedSeconds: number): string {
    if (totalElapsedSeconds < 60) {
        return `${totalElapsedSeconds}s elapsed`
    }
    const minutes = Math.floor(totalElapsedSeconds / 60)
    const seconds = totalElapsedSeconds % 60
    return `${minutes}m ${seconds}s elapsed`
}

function isTerminalBuildStage(stage: string): boolean {
    return COMPONENT_TERMINAL_BUILD_STAGES.includes(stage as typeof COMPONENT_TERMINAL_BUILD_STAGES[number])
}

function isTerminalRunStage(stage: string): boolean {
    return COMPONENT_TERMINAL_RUN_STAGES.includes(stage as typeof COMPONENT_TERMINAL_RUN_STAGES[number])
}

function resolveServerEngine(enginePolicy: ComponentJobEnginePolicy): ComponentServerEngine | 'auto' {
    switch (enginePolicy) {
        case 'prefer-wasmtime':
            return 'wasmtime'
        default:
            return 'auto'
    }
}

function sleep(ms: number): Promise<void> {
    return new Promise(resolve => window.setTimeout(resolve, ms))
}

function formatScheduleSummary(settings: ComponentJobSettings): string {
    if (!settings.enabled || settings.schedulePreset === 'manual') {
        return 'Manual only'
    }
    if (settings.schedulePreset === 'custom') {
        return settings.cron ? 'Custom cron' : 'Custom schedule'
    }
    return `${SCHEDULE_PRESET_LABELS[settings.schedulePreset]} schedule`
}

function formatScheduleChip(settings: ComponentJobSettings): string {
    if (!settings.enabled || settings.schedulePreset === 'manual') {
        return 'Manual only'
    }
    if (settings.schedulePreset === 'custom') {
        return 'Scheduled'
    }
    return SCHEDULE_PRESET_LABELS[settings.schedulePreset]
}

function formatScheduleMeta(settings: ComponentJobSettings): string {
    const parts = [
        'Server',
        settings.timezone,
        ENGINE_POLICY_LABELS[settings.enginePolicy],
        CONCURRENCY_POLICY_LABELS[settings.concurrencyPolicy],
    ]
    const cron = resolveComponentJobScheduleCron(settings)
    if (cron && settings.schedulePreset === 'custom') {
        parts.splice(2, 0, cron)
    }
    return parts.join(' · ')
}

function formatWebhookEvents(events: readonly ComponentWebhookEvent[]): string {
    return events.map(event => WEBHOOK_EVENT_LABELS[event]).join(' · ')
}

function encodeComponentSourcePathForRoute(sourcePath: string): string {
    return sourcePath
        .replace(/^\/+/, '')
        .split('/')
        .filter(segment => segment.length > 0)
        .map(segment => encodeURIComponent(segment))
        .join('/')
}

function buildTriggerRequestExample(
    apiBaseUrl: string,
    sourcePath: string,
    body: Record<string, unknown>,
): string {
    const requestBody = JSON.stringify(body, null, 2).replace(/'/g, "'\\''")
    return [
        'curl -X POST \\',
        `  '${resolveComponentJobsApiBaseUrl(apiBaseUrl)}/triggers/${encodeComponentSourcePathForRoute(sourcePath)}' \\`,
        "  -H 'Authorization: Bearer <workspace-write-token>' \\",
        "  -H 'Content-Type: application/json' \\",
        `  --data-raw '${requestBody}'`,
    ].join('\n')
}

function describeSelection(path: string): string {
    const parsed = parseComponentJobPath(path)
    if (!parsed) return 'Source'
    switch (parsed.kind) {
        case 'job_root':
            return 'Overview'
        case 'source':
            return 'Source'
        case 'settings':
            return 'Settings'
        case 'run_metadata':
            return `Run ${parsed.runId} metadata`
        case 'run_stdout':
            return `Run ${parsed.runId} stdout`
        case 'run_stderr':
            return `Run ${parsed.runId} stderr`
        case 'run_events':
            return `Run ${parsed.runId} events`
        case 'run_http':
            return `Run ${parsed.runId} HTTP`
        case 'run_mcp':
            return `Run ${parsed.runId} MCP`
        case 'run_output':
            return isRunProbeOutputPath(path) ? `Run ${parsed.runId} browser probe` : `Run ${parsed.runId} output`
        case 'run_dir':
        case 'run_outputs_dir':
        case 'run_file':
            return `Run ${parsed.runId}`
        case 'builds_dir':
            return 'Build history'
        case 'build_attempts_dir':
            return 'Failed build attempts'
        case 'build_attempt_metadata':
            return `Build ${shortBuildId(parsed.buildId ?? '')} metadata`
        case 'build_attempt_build_log':
            return `Build ${shortBuildId(parsed.buildId ?? '')} log`
        case 'build_attempt_output':
            return `Build ${shortBuildId(parsed.buildId ?? '')} browser probe`
        case 'build_attempt_dir':
        case 'build_attempt_outputs_dir':
        case 'build_attempt_file':
            return `Build ${shortBuildId(parsed.buildId ?? '')}`
        case 'artifact_manifest':
            return `Build ${shortArtifactId(parsed.artifactId ?? '')} manifest`
        case 'artifact_component_wasm':
            return `Build ${shortArtifactId(parsed.artifactId ?? '')} component`
        case 'artifact_build_log':
            return `Build ${shortArtifactId(parsed.artifactId ?? '')} log`
        case 'artifact_dir':
        case 'artifact_file':
            return `Build ${shortArtifactId(parsed.artifactId ?? '')}`
        default:
            return 'Component Job'
    }
}

function getRunInspectorTabFromSelection(path: string): RunInspectorTabId | null {
    const parsed = parseComponentJobPath(path)
    if (!parsed) return null

    switch (parsed.kind) {
        case 'run_stdout':
            return 'stdout'
        case 'run_stderr':
            return 'stderr'
        case 'run_events':
            return 'events'
        case 'run_http':
            return 'http'
        case 'run_mcp':
            return 'mcp'
        case 'run_output':
            if (isRunProbeOutputPath(path)) return 'probe'
            return parseRunTranscriptSession(path) ? 'transcript' : 'outputs'
        case 'run_outputs_dir':
            return 'outputs'
        case 'run_dir':
        case 'run_metadata':
        case 'run_file':
            return 'overview'
        default:
            return null
    }
}

function getRunInspectorLabel(tab: RunInspectorTabId): string {
    switch (tab) {
        case 'overview':
            return 'Run JSON'
        case 'stdout':
            return 'stdout'
        case 'stderr':
            return 'stderr'
        case 'events':
            return 'Events'
        case 'http':
            return 'HTTP'
        case 'mcp':
            return 'MCP'
        case 'probe':
            return 'Browser probe'
        case 'transcript':
            return 'Agent transcript'
        case 'outputs':
            return 'Outputs'
        case 'handlers':
            return 'Handlers'
        case 'deliveries':
            return 'Deliveries'
        case 'live-runtime-trace':
            return 'Live trace'
        default:
            return 'Run'
    }
}

function getRunOutputLabel(path: string): string {
    const parsed = parseComponentJobPath(path)
    if (parsed?.kind === 'run_output' && parsed.relativePath) {
        return parsed.relativePath
    }
    return path.split('/').pop() ?? path
}

function getRunTranscriptLabel(path: string): string {
    const session = parseRunTranscriptSession(path)
    return session?.sessionId ?? (path.split('/').slice(-2, -1)[0] ?? path)
}

function deriveFolderFirstConfigEntry(sourcePath: string | null, jobRootPath: string | null): string | null {
    if (!sourcePath || !jobRootPath) return null
    const fileName = sourcePath.split('/').pop()
    return fileName ? `./${fileName}` : null
}

async function loadRunHistoryItem(
    sourcePath: string,
    file: FileStat,
    readFile: (path: string) => Promise<string | null>,
    fileSet: Set<string>,
    outputCounts: Map<string, number>,
): Promise<RunHistoryItem> {
    const parsed = parseComponentJobPath(file.path)
    if (!parsed?.runId) {
        throw new Error(`Invalid component run metadata path: ${file.path}`)
    }

    const runPaths = getComponentRunPaths(sourcePath, parsed.runId)
    const record = safeParseJson(await readFile(file.path))
    const startedAt = pickTimestamp(record, ['startedAt', 'startedAtMs', 'createdAt', 'timestamp']) ?? file.mtime
    const status = pickString(record, ['status', 'state', 'result', 'outcome']) ?? 'completed'
    const target = pickString(record, ['target', 'runtimeTarget', 'targetKind']) ?? 'server'
    const engine = pickString(record, ['engine', 'engineId', 'recommendedServerEngine'])
    const summary = pickString(record, ['summary', 'resultSummary', 'message'])
    const artifactId = pickString(record, ['artifactId'])

    return {
        runId: parsed.runId,
        metadataPath: file.path,
        startedAt,
        startedAtLabel: formatHistoryTimestamp(startedAt),
        status,
        statusLabel: formatStatusLabel(status),
        target,
        engine,
        summary,
        artifactId,
        stdoutPath: fileSet.has(runPaths.stdout) ? runPaths.stdout : undefined,
        stderrPath: fileSet.has(runPaths.stderr) ? runPaths.stderr : undefined,
        eventsPath: fileSet.has(runPaths.events) ? runPaths.events : undefined,
        httpPath: fileSet.has(runPaths.http) ? runPaths.http : undefined,
        mcpPath: fileSet.has(runPaths.mcp) ? runPaths.mcp : undefined,
        outputFileCount: outputCounts.get(parsed.runId) ?? 0,
    }
}

async function loadBuildHistoryItem(
    sourcePath: string,
    file: FileStat,
    readFile: (path: string) => Promise<string | null>,
    fileSet: Set<string>,
): Promise<BuildHistoryItem> {
    const parsed = parseComponentJobPath(file.path)
    if (!parsed?.artifactId) {
        throw new Error(`Invalid component build manifest path: ${file.path}`)
    }

    const buildPaths = getComponentJobBuildPaths(sourcePath, parsed.artifactId)
    const record = safeParseJson(await readFile(file.path))
    const createdAt = pickTimestamp(record, ['createdAt', 'builtAt', 'timestamp']) ?? file.mtime
    const status = pickString(record, ['status']) ?? 'ready'
    const recommendedServerEngine = pickString(record, ['recommendedServerEngine', 'engine'])
    const artifactFiles = normalizeBuildArtifactFiles(record, buildPaths, fileSet)
    const byteSizes = artifactFiles
        .map(artifactFile => artifactFile.size)
        .filter((size): size is number => typeof size === 'number' && Number.isFinite(size) && size >= 0)
    const totalFileBytes = byteSizes.length > 0
        ? byteSizes.reduce((sum, size) => sum + size, 0)
        : null

    return {
        artifactId: parsed.artifactId,
        manifestPath: file.path,
        createdAt,
        createdAtLabel: formatHistoryTimestamp(createdAt),
        status,
        statusLabel: formatStatusLabel(status),
        recommendedServerEngine,
        componentWasmPath: fileSet.has(buildPaths.componentWasm) ? buildPaths.componentWasm : undefined,
        buildLogPath: fileSet.has(buildPaths.buildLog) ? buildPaths.buildLog : undefined,
        sourceSnapshot: pickString(record, ['sourceSnapshot']),
        buildTarget: formatBuildTarget(record),
        entrypoints: normalizeStringArray(record?.entrypoints),
        worldVersion: pickString(record, ['worldVersion']),
        artifactFiles,
        totalFileBytes,
    }
}

function normalizeBuildArtifactFiles(
    record: JsonObject | null,
    buildPaths: ReturnType<typeof getComponentJobBuildPaths>,
    fileSet: Set<string>,
): BuildArtifactFileItem[] {
    const entries = Array.isArray(record?.files) ? record.files : []
    const normalized: BuildArtifactFileItem[] = []
    const seen = new Set<string>()

    const addFile = (path: string, size?: number, sha256?: string) => {
        const normalizedPath = path.trim().replace(/^\/+/, '')
        if (!normalizedPath || seen.has(normalizedPath)) return
        seen.add(normalizedPath)
        const workspacePath = `${buildPaths.artifactDir}/${normalizedPath}`
        normalized.push({
            path: normalizedPath,
            workspacePath,
            size,
            sha256,
            available: fileSet.has(workspacePath),
        })
    }

    addFile('manifest.json')

    for (const entry of entries) {
        if (!entry || typeof entry !== 'object' || Array.isArray(entry)) continue
        const path = typeof (entry as { path?: unknown }).path === 'string'
            ? (entry as { path: string }).path
            : ''
        const size = typeof (entry as { size?: unknown }).size === 'number'
            ? (entry as { size: number }).size
            : undefined
        const sha256 = typeof (entry as { sha256?: unknown }).sha256 === 'string'
            ? (entry as { sha256: string }).sha256
            : undefined
        addFile(path, size, sha256)
    }

    return normalized
}

function normalizeStringArray(value: unknown): string[] {
    if (!Array.isArray(value)) return []
    return value.filter((entry): entry is string => typeof entry === 'string' && entry.trim().length > 0)
}

function formatBuildTarget(record: JsonObject | null): string | undefined {
    const buildTarget = pickJsonObject(record, ['buildTarget'])
    if (!buildTarget) return undefined
    const kind = pickString(buildTarget, ['kind'])
    const declaredPath = pickString(buildTarget, ['declaredPath'])
    return [kind, declaredPath].filter((value): value is string => typeof value === 'string' && value.length > 0).join(' · ') || undefined
}

async function loadFailedBuildHistoryItem(
    sourcePath: string,
    file: FileStat,
    readFile: (path: string) => Promise<string | null>,
    fileSet: Set<string>,
): Promise<FailedBuildAttemptItem> {
    const parsed = parseComponentJobPath(file.path)
    if (!parsed?.buildId) {
        throw new Error(`Invalid failed component build metadata path: ${file.path}`)
    }

    const buildPaths = getComponentBuildAttemptPaths(sourcePath, parsed.buildId)
    const probePath = `${buildPaths.outputsDir}/probe/probe.json`
    const record = safeParseJson(await readFile(file.path))
    const createdAt = pickTimestamp(record, ['updatedAt', 'stageStartedAt', 'startedAt', 'timestamp']) ?? file.mtime
    const status = pickString(record, ['status', 'stage']) ?? 'failed'
    const summary = pickString(record, ['message', 'summary'])
    const error = pickString(record, ['error'])

    return {
        buildId: parsed.buildId,
        metadataPath: file.path,
        createdAt,
        createdAtLabel: formatHistoryTimestamp(createdAt),
        status,
        statusLabel: formatStatusLabel(status),
        summary,
        error,
        buildLogPath: fileSet.has(buildPaths.buildLog) ? buildPaths.buildLog : undefined,
        probePath: fileSet.has(probePath) ? probePath : undefined,
    }
}

function ComponentJobSettingsDialog({ sourceName, initialSettings, isSaving, onClose, onSave }: ComponentJobSettingsDialogProps) {
    const [draft, setDraft] = useState<ComponentJobSettings>(initialSettings)
    const [error, setError] = useState<string | null>(null)

    const schedulePreview = draft.enabled ? resolveComponentJobScheduleCron(draft) : null

    const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault()
        if (draft.schedulePreset === 'custom' && draft.enabled && draft.cron.trim().length === 0) {
            setError('Custom cron requires an expression.')
            return
        }
        if (draft.timezone.trim().length === 0) {
            setError('Timezone is required.')
            return
        }
        setError(null)
        void onSave(draft)
    }

    return (
        <div className="component-job-modal-shell" onClick={onClose}>
            <div className="component-job-modal-backdrop" />
            <form
                className="component-job-modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby="component-job-schedule-title"
                onClick={event => event.stopPropagation()}
                onSubmit={handleSubmit}
            >
                <div className="component-job-modal-header">
                    <div>
                        <p className="component-job-kicker">Schedule</p>
                        <h2 id="component-job-schedule-title">{sourceName}</h2>
                    </div>
                    <button type="button" className="btn btn-sm" onClick={onClose} disabled={isSaving}>
                        Close
                    </button>
                </div>

                <div className="component-job-modal-body">
                    <label className="component-job-toggle-field">
                        <input
                            type="checkbox"
                            checked={draft.enabled}
                            onChange={event => setDraft(current => ({ ...current, enabled: event.target.checked }))}
                            disabled={isSaving}
                        />
                        <div>
                            <strong>Enabled</strong>
                            <span>Persist a server-side schedule for this source file.</span>
                        </div>
                    </label>

                    <div className="component-job-form-grid">
                        <div className="component-job-form-field">
                            <label>Target</label>
                            <div className="component-job-static-value">Server</div>
                        </div>

                        <div className="component-job-form-field">
                            <label htmlFor="component-job-timezone">Timezone</label>
                            <input
                                id="component-job-timezone"
                                className="component-job-input"
                                value={draft.timezone}
                                onChange={event => setDraft(current => ({ ...current, timezone: event.target.value }))}
                                placeholder="UTC"
                                disabled={isSaving}
                            />
                        </div>
                    </div>

                    <div className="component-job-form-field">
                        <label>Frequency</label>
                        <div className="component-job-preset-grid">
                            {COMPONENT_JOB_SCHEDULE_PRESETS.map(preset => (
                                <button
                                    key={preset}
                                    type="button"
                                    className={`component-job-preset-btn${draft.schedulePreset === preset ? ' active' : ''}`}
                                    aria-pressed={draft.schedulePreset === preset}
                                    onClick={() => setDraft(current => ({ ...current, schedulePreset: preset }))}
                                    disabled={isSaving}
                                >
                                    {SCHEDULE_PRESET_LABELS[preset]}
                                </button>
                            ))}
                        </div>
                    </div>

                    {draft.schedulePreset === 'custom' && (
                        <div className="component-job-form-field">
                            <label htmlFor="component-job-cron">Custom cron expression</label>
                            <input
                                id="component-job-cron"
                                className="component-job-input"
                                value={draft.cron}
                                onChange={event => setDraft(current => ({ ...current, cron: event.target.value }))}
                                placeholder="15 4 * * *"
                                disabled={isSaving}
                            />
                        </div>
                    )}

                    <div className="component-job-form-grid">
                        <div className="component-job-form-field">
                            <label htmlFor="component-job-engine-policy">Engine policy</label>
                            <select
                                id="component-job-engine-policy"
                                className="component-job-input"
                                value={draft.enginePolicy}
                                onChange={event => setDraft(current => ({
                                    ...current,
                                    enginePolicy: event.target.value as ComponentJobEnginePolicy,
                                }))}
                                disabled={isSaving}
                            >
                                {COMPONENT_JOB_ENGINE_POLICIES.map(policy => (
                                    <option key={policy} value={policy}>
                                        {ENGINE_POLICY_LABELS[policy]}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="component-job-form-field">
                            <label htmlFor="component-job-concurrency-policy">Concurrency policy</label>
                            <select
                                id="component-job-concurrency-policy"
                                className="component-job-input"
                                value={draft.concurrencyPolicy}
                                onChange={event => setDraft(current => ({
                                    ...current,
                                    concurrencyPolicy: event.target.value as ComponentJobConcurrencyPolicy,
                                }))}
                                disabled={isSaving}
                            >
                                {COMPONENT_JOB_CONCURRENCY_POLICIES.map(policy => (
                                    <option key={policy} value={policy}>
                                        {CONCURRENCY_POLICY_LABELS[policy]}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div className="component-job-form-field">
                        <label htmlFor="component-job-payload">Run input / payload preset</label>
                        <textarea
                            id="component-job-payload"
                            className="component-job-input component-job-textarea"
                            value={draft.payloadPreset}
                            onChange={event => setDraft(current => ({ ...current, payloadPreset: event.target.value }))}
                            placeholder='{"dryRun":true}'
                            rows={5}
                            disabled={isSaving}
                        />
                    </div>

                    <div className="component-job-preview-card">
                        <strong>{formatScheduleSummary(draft)}</strong>
                        <span>{formatScheduleMeta(draft)}</span>
                        {schedulePreview && <code>{schedulePreview}</code>}
                    </div>

                    {error && <p className="component-job-form-error">{error}</p>}
                </div>

                <div className="component-job-modal-actions">
                    <button type="button" className="btn btn-sm" onClick={onClose} disabled={isSaving}>
                        Cancel
                    </button>
                    <button type="submit" className="btn btn-sm" disabled={isSaving}>
                        {isSaving ? 'Saving…' : 'Save schedule'}
                    </button>
                </div>
            </form>
        </div>
    )
}

function ComponentJobStarterDialog({
    sourceName,
    isFolderFirstJob,
    scaffolds,
    isApplying,
    onClose,
    onApply,
}: ComponentJobStarterDialogProps) {
    const [selectedTemplate, setSelectedTemplate] = useState<ComponentJobStarterTemplate>(
        scaffolds[0]?.template ?? 'container-service',
    )

    useEffect(() => {
        if (!scaffolds.some(scaffold => scaffold.template === selectedTemplate)) {
            setSelectedTemplate(scaffolds[0]?.template ?? 'container-service')
        }
    }, [scaffolds, selectedTemplate])

    const selectedScaffold = scaffolds.find(scaffold => scaffold.template === selectedTemplate) ?? scaffolds[0] ?? null
    const targetCopy = isFolderFirstJob
        ? 'Choose a scaffold preset for this folder-first job. job.json and .secureblob internals stay untouched.'
        : 'Choose a source-compatible starter for this file. Convert to a job folder to unlock the multi-file preset.'
    const previewPaths = selectedScaffold ? selectedScaffold.files.map(file => file.path) : []
    const previewDetails = selectedScaffold?.directories.length
        ? `${previewPaths.join(', ')} | dirs: ${selectedScaffold.directories.join(', ')}`
        : previewPaths.join(', ')

    const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault()
        if (!selectedScaffold) {
            return
        }
        onApply(selectedScaffold.template)
    }

    return (
        <div className="component-job-modal-shell" onClick={onClose}>
            <div className="component-job-modal-backdrop" />
            <form
                className="component-job-modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby="component-job-starter-title"
                onClick={event => event.stopPropagation()}
                onSubmit={handleSubmit}
            >
                <div className="component-job-modal-header">
                    <div>
                        <p className="component-job-kicker">Starter</p>
                        <h2 id="component-job-starter-title">{sourceName}</h2>
                    </div>
                    <button type="button" className="btn btn-sm" onClick={onClose} disabled={isApplying}>
                        Close
                    </button>
                </div>

                <div className="component-job-modal-body">
                    <p className="component-job-empty-copy">{targetCopy}</p>

                    <div className="component-job-form-field">
                        <label>Starter preset</label>
                        <div className="component-job-preset-grid">
                            {scaffolds.map(scaffold => (
                                <button
                                    key={scaffold.template}
                                    type="button"
                                    className={`component-job-preset-btn${selectedTemplate === scaffold.template ? ' active' : ''}`}
                                    aria-pressed={selectedTemplate === scaffold.template}
                                    onClick={() => setSelectedTemplate(scaffold.template)}
                                    disabled={isApplying}
                                >
                                    {scaffold.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    {selectedScaffold && (
                        <div className="component-job-preview-card">
                            <strong>{selectedScaffold.label}</strong>
                            <span>{selectedScaffold.description}</span>
                            {previewDetails && <code>{previewDetails}</code>}
                        </div>
                    )}
                </div>

                <div className="component-job-modal-actions">
                    <button type="button" className="btn btn-sm" onClick={onClose} disabled={isApplying}>
                        Cancel
                    </button>
                    <button type="submit" className="btn btn-sm" disabled={isApplying || !selectedScaffold}>
                        {isApplying ? 'Applying…' : 'Apply starter'}
                    </button>
                </div>
            </form>
        </div>
    )
}

function ComponentJobConfirmDialog({
    state,
    isSubmitting,
    onCancel,
}: {
    state: ComponentJobConfirmState
    isSubmitting: boolean
    onCancel: () => void
}) {
    return (
        <div className="component-job-modal-shell" onClick={onCancel}>
            <div className="component-job-modal-backdrop" />
            <div
                className="component-job-modal"
                role="dialog"
                aria-modal="true"
                aria-label={state.title}
                onClick={event => event.stopPropagation()}
            >
                <div className="component-job-modal-header">
                    <div>
                        <p className="component-job-kicker">Confirm</p>
                        <h2>{state.title}</h2>
                    </div>
                </div>

                <div className="component-job-modal-body">
                    <p className="component-job-empty-copy">{state.message}</p>
                </div>

                <div className="component-job-modal-actions">
                    <button type="button" className="btn btn-sm" onClick={onCancel} disabled={isSubmitting}>
                        Cancel
                    </button>
                    <button type="button" className="btn btn-sm" onClick={state.onConfirm} disabled={isSubmitting}>
                        {isSubmitting ? 'Applying…' : state.confirmLabel}
                    </button>
                </div>
            </div>
        </div>
    )
}

export function ComponentJobPanel({
    path,
    onOpenFile,
    onOpenFileInEditMode,
    onEditFile,
    canWrite = true,
}: ComponentJobPanelProps) {
    const engineManager = useEngineManager()
    const workspace = useWorkspace()
    const { files, readFile, writeFile, mkdir, loadDir } = useFileSystem()
    const { entries } = useRegistry()
    const { blobId: workspaceBlobId } = useWorkspaceMetadata()
    const toast = useToast()
    const browserTimeZone = useMemo(() => getBrowserTimeZone(), [])
    const sourcePath = useMemo(() => resolveComponentJobSourcePath(path, files), [path, files])
    const sourceFileName = sourcePath?.split('/').pop() ?? 'main.tsx'
    const jobRootPath = useMemo(
        () => getComponentJobRootPath(path) ?? (sourcePath ? getComponentJobRootPath(sourcePath) : null),
        [path, sourcePath],
    )
    const parsedSelection = useMemo(() => (sourcePath ? parseComponentJobPath(path) : null), [path, sourcePath])

    // When opened on a folder-first .job root and the source file is not yet
    // present in the global file list (on-demand loading), trigger a directory
    // load so resolveComponentJobSourcePath can find the real main.* entry.
    useEffect(() => {
        if (!jobRootPath || !sourcePath) return
        const fileSet = new Set(files.filter(f => f.kind === 'file').map(f => f.path))
        if (!fileSet.has(sourcePath)) {
            void loadDir(jobRootPath)
        }
    }, [jobRootPath, sourcePath, files, loadDir])
    const [runHistory, setRunHistory] = useState<RunHistoryItem[]>([])
    const [buildHistory, setBuildHistory] = useState<BuildHistoryItem[]>([])
    const [failedBuildHistory, setFailedBuildHistory] = useState<FailedBuildAttemptItem[]>([])
    const [settings, setSettings] = useState<ComponentJobSettings>(() => (
        createDefaultComponentJobSettings({ fallbackTimezone: browserTimeZone })
    ))
    const [configEntry, setConfigEntry] = useState<string | null>(null)
    const [serverRunner, setServerRunner] = useState<ComponentJobServerRunner | null>(null)
    const [isScheduleOpen, setIsScheduleOpen] = useState(false)
    const [isScheduleSaving, setIsScheduleSaving] = useState(false)
    const [isStarterOpen, setIsStarterOpen] = useState(false)
    const [isStarterApplying, setIsStarterApplying] = useState(false)
    const [starterConfirm, setStarterConfirm] = useState<ComponentJobConfirmState | null>(null)
    const [selectedRunId, setSelectedRunId] = useState<string | null>(null)
    const [selectedArtifactId, setSelectedArtifactId] = useState<string | null>(null)
    const [selectedFailedBuildId, setSelectedFailedBuildId] = useState<string | null>(null)
    const [activeRunTab, setActiveRunTab] = useState<RunInspectorTabId>('overview')
    const [selectedOutputPath, setSelectedOutputPath] = useState<string | null>(null)
    const [selectedTranscriptPath, setSelectedTranscriptPath] = useState<string | null>(null)
    const [runInspectorContent, setRunInspectorContent] = useState<string | null>(null)
    const [runInspectorError, setRunInspectorError] = useState<string | null>(null)
    const [isRunInspectorLoading, setIsRunInspectorLoading] = useState(false)
    const [buildProbeContent, setBuildProbeContent] = useState<string | null>(null)
    const [buildProbeError, setBuildProbeError] = useState<string | null>(null)
    const [runInputDraft, setRunInputDraft] = useState('')
    const [handlerInputDraft, setHandlerInputDraft] = useState('{}')
    const [isHandlerPending, setIsHandlerPending] = useState(false)
    const [handlerError, setHandlerError] = useState<string | null>(null)
    const [handlerInvocationHistory, setHandlerInvocationHistory] = useState<HandlerInvocationHistoryItem[]>([])
    const [selectedHandlerInvocationId, setSelectedHandlerInvocationId] = useState<string | null>(null)
    const [sourceText, setSourceText] = useState<string | null>(null)
    const [liveBuild, setLiveBuild] = useState<ComponentBuildStatusRecord | null>(null)
    const [liveRun, setLiveRun] = useState<ComponentRunStatusRecord | null>(null)
    const [controlPlaneError, setControlPlaneError] = useState<string | null>(null)
    const [isBuildPending, setIsBuildPending] = useState(false)
    const [isRunPending, setIsRunPending] = useState(false)
    const [isCancelPending, setIsCancelPending] = useState(false)
    const [webhooks, setWebhooks] = useState<ComponentWebhookRegistrationRecord[]>([])
    const [webhookForm, setWebhookForm] = useState<ComponentWebhookFormState>({
        callbackUrl: '',
        secret: '',
        events: ['run.completed'],
    })
    const [isWebhooksLoading, setIsWebhooksLoading] = useState(false)
    const [isWebhookSaving, setIsWebhookSaving] = useState(false)
    const [deletingWebhookId, setDeletingWebhookId] = useState<string | null>(null)
    const [webhookError, setWebhookError] = useState<string | null>(null)
    const [webhookDeliveries, setWebhookDeliveries] = useState<ComponentWebhookWithDeliveriesRecord | null>(null)
    const [isWebhookDeliveriesLoading, setIsWebhookDeliveriesLoading] = useState(false)
    const [triggerExampleCopied, setTriggerExampleCopied] = useState(false)
    const [appIacStatus, setAppIacStatus] = useState<SecureBlobAppIacStatusResult | null>(null)
    const [isAppIacStatusLoading, setIsAppIacStatusLoading] = useState(false)
    const [appIacStatusError, setAppIacStatusError] = useState<string | null>(null)
    const [isAppIacPreparingAccess, setIsAppIacPreparingAccess] = useState(false)
    const [httpWaterfallMode, setHttpWaterfallMode] = useState(false)
    // §263: live runtime trace state — polled from the incremental live-trace file.
    const [liveTraceEvents, setLiveTraceEvents] = useState<readonly RuntimeEventEntry[]>([])
    const [liveTraceFirstEventArrived, setLiveTraceFirstEventArrived] = useState(false)
    const isMountedRef = useRef(true)
    const hasInitializedTriggerRequestExampleRef = useRef(false)
    const buildPollSerialRef = useRef(0)
    const runPollSerialRef = useRef(0)
    const runMaterializationRef = useRef<Record<string, RunMaterializationState>>({})
    const buildProbeMarkerRef = useRef<ComponentJobRunProbeCaptureMarker | null>(null)
    const runProbeMarkerRef = useRef<ComponentJobRunProbeCaptureMarker | null>(null)
    const persistedBuildProbeIdsRef = useRef<Set<string>>(new Set())
    const persistedRunProbeIdsRef = useRef<Set<string>>(new Set())

    const sourceName = sourcePath?.split('/').pop() ?? path.split('/').pop() ?? path
    const isFolderFirstJob = !!jobRootPath
    const jobName = isFolderFirstJob
        ? (jobRootPath?.split('/').pop() ?? sourceName).replace(/\.job$/i, '')
        : sourceName
    const starterTargetName = isFolderFirstJob
        ? (jobRootPath?.split('/').pop() ?? sourceName)
        : sourceName
    const jobPathLabel = jobRootPath ?? sourcePath ?? path
    const jobDisplayName = isFolderFirstJob ? jobName : sourceName
    const companionPaths = useMemo(
        () => (sourcePath ? getComponentJobCompanionPaths(sourcePath) : null),
        [sourcePath],
    )
    const fileSet = useMemo(() => new Set(files.filter(file => file.kind === 'file').map(file => file.path)), [files])
    const starterScaffolds = useMemo(() => {
        if (jobRootPath) {
            return listComponentJobStarterScaffoldsForJobRoot(jobRootPath)
        }
        if (sourcePath) {
            return listComponentJobStarterScaffoldsForSourceFile(sourcePath)
        }
        return []
    }, [jobRootPath, sourcePath])
    const settingsExists = companionPaths ? fileSet.has(companionPaths.settings) : false
    const settingsMtime = useMemo(() => {
        if (!companionPaths) return 0
        return files.find(file => file.path === companionPaths.settings)?.mtime ?? 0
    }, [companionPaths, files])
    const sourceMtime = useMemo(
        () => (sourcePath ? files.find(file => file.path === sourcePath)?.mtime ?? 0 : 0),
        [files, sourcePath],
    )
    const currentWorkspaceEntry = useMemo(
        () => entries.find(entry => entry.blobId === workspaceBlobId) ?? null,
        [entries, workspaceBlobId],
    )
    const canInvokeHandler = !!sourcePath && !!currentWorkspaceEntry && isLikelyHandlerSourcePath(sourcePath)
    const handlerInputValidation = useMemo(
        () => validateHandlerInputDraft(handlerInputDraft),
        [handlerInputDraft],
    )
    const componentJobsClient = useMemo(
        () => (currentWorkspaceEntry ? createComponentJobsClientOptions(currentWorkspaceEntry) : null),
        [currentWorkspaceEntry],
    )
    const workspaceWriter = useMemo(() => ({
        writeText: (targetPath: string, content: string) => writeFile(targetPath, content),
        writeBytes: (targetPath: string, content: Uint8Array) => workspace.fs.writeBytes(targetPath, content),
    }), [workspace, writeFile])
    const appIacStatusCredential = useMemo<AppIacStatusCredential | null>(() => {
        if (!isFolderFirstJob || !currentWorkspaceEntry) {
            return null
        }

        if (serverRunner?.connectionCredential) {
            return {
                token: serverRunner.connectionCredential,
                label: serverRunner.label,
                configBlobId: serverRunner.configBlobId,
                source: 'server-runner',
            }
        }

        const connection = selectAppIacMcpConnection(engineManager.getMcpConnections(), workspaceBlobId)
        if (!connection) {
            return null
        }

        return {
            token: generateConfigConnectionToken(
                connection,
                currentWorkspaceEntry.apiBaseUrl.trim().replace(/\/+$/, ''),
            ),
            label: connection.label,
            configBlobId: connection.configBlobId,
            source: 'mcp-connection',
        }
    }, [currentWorkspaceEntry, engineManager, isFolderFirstJob, serverRunner, workspaceBlobId])
    const activeHandlerInvocation = useMemo(
        () => handlerInvocationHistory.find(invocation => invocation.id === selectedHandlerInvocationId)
            ?? handlerInvocationHistory[0]
            ?? null,
        [handlerInvocationHistory, selectedHandlerInvocationId],
    )
    const activeHandlerInvocationOutput = useMemo(() => {
        if (!activeHandlerInvocation || typeof activeHandlerInvocation.output === 'undefined') {
            return null
        }

        return JSON.stringify(activeHandlerInvocation.output, null, 2)
    }, [activeHandlerInvocation])

    const runMetadataFiles = useMemo(() => {
        if (!companionPaths) return []
        return files
            .filter(file => file.kind === 'file' && file.path.startsWith(companionPaths.runsDir + '/') && file.path.endsWith('/run.json'))
            .sort((left, right) => right.mtime - left.mtime)
    }, [companionPaths, files])

    const buildManifestFiles = useMemo(() => {
        if (!companionPaths) return []
        return files
            .filter(file => file.kind === 'file' && file.path.startsWith(companionPaths.buildsDir + '/') && file.path.endsWith('/manifest.json'))
            .sort((left, right) => right.mtime - left.mtime)
    }, [companionPaths, files])

    const failedBuildMetadataFiles = useMemo(() => {
        if (!companionPaths) return []
        return files
            .filter(file => file.kind === 'file' && file.path.startsWith(`${companionPaths.buildsDir}/_attempts/`) && file.path.endsWith('/build.json'))
            .sort((left, right) => right.mtime - left.mtime)
    }, [companionPaths, files])

    const outputFilesByRunId = useMemo(() => {
        const grouped = new Map<string, RunOutputFile[]>()
        if (!companionPaths || !sourcePath) return grouped

        for (const file of files) {
            if (file.kind !== 'file') continue
            const parsed = parseComponentJobPath(file.path)
            if (parsed?.sourcePath !== sourcePath || parsed.kind !== 'run_output' || !parsed.runId) {
                continue
            }

            const current = grouped.get(parsed.runId) ?? []
            current.push({
                path: file.path,
                label: getRunOutputLabel(file.path),
                mtime: file.mtime,
            })
            grouped.set(parsed.runId, current)
        }

        for (const outputFiles of grouped.values()) {
            outputFiles.sort((left, right) => {
                if (right.mtime !== left.mtime) return right.mtime - left.mtime
                return left.label.localeCompare(right.label)
            })
        }

        return grouped
    }, [companionPaths, files, sourcePath])

    const outputCounts = useMemo(() => {
        const counts = new Map<string, number>()
        for (const [runId, outputFiles] of outputFilesByRunId.entries()) {
            counts.set(runId, outputFiles.length)
        }
        return counts
    }, [outputFilesByRunId])

    useEffect(() => {
        return () => {
            isMountedRef.current = false
            buildPollSerialRef.current += 1
            runPollSerialRef.current += 1
        }
    }, [])

    useEffect(() => {
        setLiveBuild(null)
        setLiveRun(null)
        setControlPlaneError(null)
        setIsBuildPending(false)
        setIsRunPending(false)
        setIsCancelPending(false)
        setIsScheduleSaving(false)
        setWebhooks([])
        setWebhookForm({
            callbackUrl: '',
            secret: '',
            events: ['run.completed'],
        })
        setIsWebhooksLoading(false)
        setIsWebhookSaving(false)
        setDeletingWebhookId(null)
        setWebhookError(null)
        setTriggerExampleCopied(false)
        setAppIacStatus(null)
        setAppIacStatusError(null)
        setIsAppIacStatusLoading(false)
        setIsAppIacPreparingAccess(false)
        setRunInputDraft('')
        setHandlerInputDraft('{}')
        setIsHandlerPending(false)
        setHandlerError(null)
        setHandlerInvocationHistory([])
        setSelectedHandlerInvocationId(null)
        setSourceText(null)
        setConfigEntry(deriveFolderFirstConfigEntry(sourcePath, jobRootPath))
        setFailedBuildHistory([])
        setSelectedFailedBuildId(null)
        setBuildProbeContent(null)
        setBuildProbeError(null)
        buildPollSerialRef.current += 1
        runPollSerialRef.current += 1
        runMaterializationRef.current = {}
        buildProbeMarkerRef.current = null
        runProbeMarkerRef.current = null
        persistedBuildProbeIdsRef.current = new Set()
        persistedRunProbeIdsRef.current = new Set()
    }, [jobRootPath, sourcePath])

    useEffect(() => {
        setRunInputDraft(current => (current.trim().length > 0 ? current : settings.payloadPreset))
    }, [settings.payloadPreset])

    useEffect(() => {
        if (!sourcePath) {
            setRunHistory([])
            setBuildHistory([])
            setFailedBuildHistory([])
            return
        }

        let cancelled = false

        const load = async () => {
            const [runs, builds, failedBuilds] = await Promise.all([
                Promise.all(runMetadataFiles.map(file => loadRunHistoryItem(sourcePath, file, readFile, fileSet, outputCounts))),
                Promise.all(buildManifestFiles.map(file => loadBuildHistoryItem(sourcePath, file, readFile, fileSet))),
                Promise.all(failedBuildMetadataFiles.map(file => loadFailedBuildHistoryItem(sourcePath, file, readFile, fileSet))),
            ])

            if (cancelled) return

            setRunHistory(runs.sort((left, right) => right.startedAt - left.startedAt))
            setBuildHistory(builds.sort((left, right) => right.createdAt - left.createdAt))
            setFailedBuildHistory(failedBuilds.sort((left, right) => right.createdAt - left.createdAt))
        }

        load().catch(() => {
            if (!cancelled) {
                setRunHistory([])
                setBuildHistory([])
                setFailedBuildHistory([])
            }
        })

        return () => {
            cancelled = true
        }
    }, [buildManifestFiles, failedBuildMetadataFiles, fileSet, outputCounts, readFile, runMetadataFiles, sourcePath])

    useEffect(() => {
        if (!companionPaths) {
            setSettings(createDefaultComponentJobSettings({ fallbackTimezone: browserTimeZone }))
            setConfigEntry(deriveFolderFirstConfigEntry(sourcePath, jobRootPath))
            setServerRunner(null)
            return
        }

        let cancelled = false

        readFile(companionPaths.settings)
            .then(text => {
                if (!cancelled) {
                    const parsedConfig = parseComponentJobConfig(text, { fallbackTimezone: browserTimeZone })
                    setSettings(parsedConfig.schedule)
                    setConfigEntry(parsedConfig.entry ?? deriveFolderFirstConfigEntry(sourcePath, jobRootPath))
                    setServerRunner(parsedConfig.serverRunner ?? null)
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setSettings(createDefaultComponentJobSettings({ fallbackTimezone: browserTimeZone }))
                    setConfigEntry(deriveFolderFirstConfigEntry(sourcePath, jobRootPath))
                    setServerRunner(null)
                }
            })

        return () => {
            cancelled = true
        }
    }, [browserTimeZone, companionPaths, jobRootPath, readFile, settingsMtime, sourcePath])

    useEffect(() => {
        if (!sourcePath) {
            setSourceText(null)
            return
        }

        let cancelled = false

        readFile(sourcePath)
            .then(text => {
                if (!cancelled) {
                    setSourceText(text)
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setSourceText(null)
                }
            })

        return () => {
            cancelled = true
        }
    }, [readFile, sourceMtime, sourcePath])

    useEffect(() => {
        if (!componentJobsClient || !sourcePath) {
            setWebhooks([])
            setIsWebhooksLoading(false)
            setWebhookError(null)
            return
        }

        let cancelled = false
        setIsWebhooksLoading(true)
        setWebhookError(null)

        getWebhooks({
            ...componentJobsClient,
            workspaceBlobId,
        })
            .then(records => {
                if (cancelled) return
                setWebhooks(records.filter(record => record.sourcePath === sourcePath))
            })
            .catch(error => {
                if (cancelled) return
                setWebhooks([])
                setWebhookError(error instanceof Error ? error.message : 'Unknown error')
            })
            .finally(() => {
                if (!cancelled) {
                    setIsWebhooksLoading(false)
                }
            })

        return () => {
            cancelled = true
        }
    }, [componentJobsClient, sourcePath, workspaceBlobId])

    const activeWebhook = webhooks[0] ?? null

    useEffect(() => {
        if (!componentJobsClient || !activeWebhook || activeRunTab !== 'deliveries') {
            setWebhookDeliveries(null)
            setIsWebhookDeliveriesLoading(false)
            return
        }

        let cancelled = false
        setIsWebhookDeliveriesLoading(true)

        getWebhookDeliveries({
            ...componentJobsClient,
            webhookId: activeWebhook.webhookId,
        })
            .then(record => {
                if (cancelled) return
                setWebhookDeliveries(record)
            })
            .catch(() => {
                if (cancelled) return
                setWebhookDeliveries(null)
            })
            .finally(() => {
                if (!cancelled) {
                    setIsWebhookDeliveriesLoading(false)
                }
            })

        return () => {
            cancelled = true
        }
    }, [activeWebhook, activeRunTab, componentJobsClient])

    const loadAppIacStatus = useCallback(async (
        credential: AppIacStatusCredential,
        signal?: AbortSignal,
    ): Promise<SecureBlobAppIacStatusResult> => {
        if (!componentJobsClient) {
            throw new Error('Component jobs control plane is unavailable for the current workspace.')
        }

        return getAppIacStatus({
            ...componentJobsClient,
            signal,
            configConnectionToken: credential.token,
            workspaceRefs: [workspaceBlobId],
            appManifestPath: APP_IAC_MANIFEST_PATH,
            mailboxRoot: APP_IAC_MAILBOX_ROOT,
        })
    }, [componentJobsClient, workspaceBlobId])

    useEffect(() => {
        if (!isFolderFirstJob || !appIacStatusCredential || !componentJobsClient) {
            setAppIacStatus(null)
            setAppIacStatusError(null)
            setIsAppIacStatusLoading(false)
            return
        }

        let cancelled = false
        const abortController = new AbortController()
        setIsAppIacStatusLoading(true)
        setAppIacStatusError(null)

        loadAppIacStatus(appIacStatusCredential, abortController.signal)
            .then(status => {
                if (cancelled) return
                setAppIacStatus(status)
                setAppIacStatusError(null)
            })
            .catch(error => {
                if (cancelled || isAbortError(error)) return
                setAppIacStatus(null)
                setAppIacStatusError(error instanceof Error ? error.message : 'Unknown error')
            })
            .finally(() => {
                if (!cancelled) {
                    setIsAppIacStatusLoading(false)
                }
            })

        return () => {
            cancelled = true
            abortController.abort()
        }
    }, [appIacStatusCredential, componentJobsClient, isFolderFirstJob, loadAppIacStatus])

    useEffect(() => {
        setIsScheduleOpen(false)
        setIsStarterOpen(false)
        setStarterConfirm(null)
        setIsStarterApplying(false)
        setSelectedRunId(parsedSelection?.runId ?? null)
        setSelectedArtifactId(parsedSelection?.artifactId ?? null)
        setSelectedFailedBuildId(parsedSelection?.buildId ?? null)
        setSelectedOutputPath(parsedSelection?.kind === 'run_output' ? parsedSelection.path : null)
        setSelectedTranscriptPath(parsedSelection?.kind === 'run_output' && parseRunTranscriptSession(parsedSelection.path) ? parsedSelection.path : null)
    }, [parsedSelection?.artifactId, parsedSelection?.buildId, parsedSelection?.kind, parsedSelection?.path, parsedSelection?.runId, path])

    const activeRun = selectedRunId
        ? runHistory.find(run => run.runId === selectedRunId) ?? runHistory[0] ?? null
        : runHistory[0] ?? null
    const activeBuild = selectedArtifactId
        ? buildHistory.find(build => build.artifactId === selectedArtifactId) ?? buildHistory[0] ?? null
        : buildHistory[0] ?? null
    const activeFailedBuild = selectedFailedBuildId
        ? failedBuildHistory.find(build => build.buildId === selectedFailedBuildId) ?? failedBuildHistory[0] ?? null
        : failedBuildHistory[0] ?? null
    const activeRunId = activeRun?.runId ?? null
    const activeBuildId = activeBuild?.artifactId ?? null
    const activeFailedBuildId = activeFailedBuild?.buildId ?? null
    const latestRunId = runHistory[0]?.runId ?? null
    const latestBuildId = buildHistory[0]?.artifactId ?? null
    const latestFailedBuildId = failedBuildHistory[0]?.buildId ?? null
    const activeRunLabel = activeRunId && activeRunId !== latestRunId ? 'Selected run' : 'Latest run'
    const activeBuildLabel = activeBuildId && activeBuildId !== latestBuildId ? 'Selected build' : 'Latest build'
    const activeFailedBuildLabel = activeFailedBuildId && activeFailedBuildId !== latestFailedBuildId ? 'Selected failed build' : 'Latest failed build'
    const activeBuildSizeLabel = activeBuild && activeBuild.totalFileBytes !== null
        ? formatArtifactBytes(activeBuild.totalFileBytes)
        : null
    const scheduleSummary = formatScheduleSummary(settings)
    const scheduleMeta = formatScheduleMeta(settings)
    const latestBuildIsStale = !!activeBuild && sourceMtime > activeBuild.createdAt
    const sourceIsEmpty = sourceText !== null && isBlankComponentJobSource(sourceText)
    const showOnboarding = sourceIsEmpty || (!settingsExists && runHistory.length === 0 && buildHistory.length === 0 && failedBuildHistory.length === 0)
    const headerEngine = liveRun?.engine
        ?? activeRun?.engine
        ?? activeBuild?.recommendedServerEngine
        ?? ENGINE_POLICY_LABELS[settings.enginePolicy]
    const buildChip = formatBuildStatusChip(liveBuild, activeBuild, latestBuildIsStale)
    const resolvedRunInput = runInputDraft.trim().length > 0
        ? runInputDraft
        : (settings.payloadPreset.trim().length > 0 ? settings.payloadPreset : null)
    const hasLiveServerRun = !!liveRun && !isTerminalRunStage(liveRun.stage)
    const runButtonDisabled = !canWrite || !componentJobsClient || isRunPending || isBuildPending
    const buildButtonDisabled = !canWrite || !componentJobsClient || isBuildPending
    const cancelButtonDisabled = !canWrite || !componentJobsClient || !hasLiveServerRun || isCancelPending
    const onboardingTitle = isFolderFirstJob
        ? (sourceIsEmpty ? 'This job is ready for its first run' : 'This job is ready')
        : (sourceIsEmpty ? 'Make this file a server job' : 'Build and run without setup files')
    const onboardingCopy = isFolderFirstJob
        ? (sourceIsEmpty
            ? `The job root is scaffolded. Add starter code to ${sourceFileName}, then Run or Build. job.json keeps config and .secureblob/ holds run history, diagnostics, and artifacts.`
            : 'This job already has a runnable entrypoint. Use Run or Build from the overview, and inspect raw operational state under .secureblob/ when you need it.')
        : (sourceIsEmpty
            ? 'This file is empty. Drop in a starter, then Build or Run. SecureBlob creates the companion config and server runner automatically on first use.'
            : 'The source file is ready. Build or Run will create .job.json and the server runner automatically the first time you use this job.')
    const overviewPath = jobRootPath ?? sourcePath
    const selectionContextCopy = isFolderFirstJob
        ? (path === overviewPath
            ? 'Folder overview'
            : path === sourcePath
                ? 'Entrypoint context'
                : 'Job context')
        : (path === sourcePath ? 'Source-first view' : 'Companion file context')
    const runHistoryEmptyCopy = isFolderFirstJob
        ? 'Run history appears under .secureblob/runs.'
        : 'Run history appears under the source file.'
    const buildHistoryEmptyCopy = isFolderFirstJob
        ? 'Build artifacts appear under .secureblob/artifacts.'
        : 'Build artifacts appear under the source file.'
    const failedBuildHistoryEmptyCopy = isFolderFirstJob
        ? 'Failed build attempts appear under .secureblob/artifacts/_attempts.'
        : 'Failed build attempts appear under the source file.'
    const triggerRequestExample = useMemo(() => {
        if (!sourcePath || !currentWorkspaceEntry) {
            return null
        }

        const requestBody: Record<string, unknown> = {
            workspaceBlobId,
            engine: resolveServerEngine(settings.enginePolicy),
        }

        if (settings.payloadPreset.trim().length > 0) {
            requestBody.runInput = settings.payloadPreset
        }

        if (!activeWebhook?.hasRunnerBootstrap) {
            requestBody.runnerBootstrap = createComponentRunnerBootstrap(currentWorkspaceEntry, serverRunner)
        }

        return buildTriggerRequestExample(currentWorkspaceEntry.apiBaseUrl, sourcePath, requestBody)
    }, [activeWebhook?.hasRunnerBootstrap, currentWorkspaceEntry, serverRunner, settings.enginePolicy, settings.payloadPreset, sourcePath, workspaceBlobId])
    const triggerExampleMeta = activeWebhook?.hasRunnerBootstrap
        ? 'This source already has a stored runner bootstrap, so external triggers can omit it.'
        : 'Include runnerBootstrap until a webhook registration has stored one for this source.'
    const appIacWorkspaceStatus = appIacStatus?.workspaces[0] ?? null
    const appIacCurrentJobStatus = jobRootPath && appIacWorkspaceStatus
        ? appIacWorkspaceStatus.componentJobs.find(job => job.jobRoot === jobRootPath) ?? null
        : null
    const appIacStatusCards = appIacCurrentJobStatus?.statusCards ?? []
    const appIacIssueSummary = summarizeAppIacIssues(appIacStatus, jobRootPath)
    const appIacStatusSourceLabel = appIacStatusCredential
        ? `${appIacStatusCredential.source === 'server-runner' ? 'server runner' : 'MCP connection'} · ${appIacStatusCredential.label}`
        : 'No config connection'

    useEffect(() => {
        if (!hasInitializedTriggerRequestExampleRef.current) {
            hasInitializedTriggerRequestExampleRef.current = true
            return
        }

        setTriggerExampleCopied(false)
    }, [triggerRequestExample])

    useEffect(() => {
        if (!activeWebhook) {
            return
        }

        setWebhookForm({
            callbackUrl: activeWebhook.callbackUrl,
            secret: '',
            events: activeWebhook.events,
        })
    }, [activeWebhook])

    const activeRunOutputFiles = useMemo(
        () => (activeRun ? outputFilesByRunId.get(activeRun.runId) ?? [] : []),
        [activeRun, outputFilesByRunId],
    )
    const activeRunProbeFile = useMemo(
        () => activeRunOutputFiles.find(file => isRunProbeOutputPath(file.path)) ?? null,
        [activeRunOutputFiles],
    )
    const activeRunTranscriptSessions = useMemo<RunTranscriptSession[]>(
        () => activeRunOutputFiles
            .map(file => {
                const session = parseRunTranscriptSession(file.path)
                if (!session) return null
                return {
                    path: file.path,
                    label: getRunTranscriptLabel(file.path),
                    sessionId: session.sessionId,
                    mtime: file.mtime,
                } satisfies RunTranscriptSession
            })
            .filter((session): session is RunTranscriptSession => session !== null),
        [activeRunOutputFiles],
    )
    const activeRunPaths = useMemo(
        () => (sourcePath && activeRun ? getComponentRunPaths(sourcePath, activeRun.runId) : null),
        [activeRun, sourcePath],
    )
    const activeRunStage = liveRun?.runId === activeRunId ? liveRun.stage : activeRun?.status ?? null
    const canUseRemoteRunLogs = !!activeRun && activeRun.target === 'server' && !!componentJobsClient
    const shouldTailActiveRunLogs = !!activeRunStage && canUseRemoteRunLogs && !isTerminalRunStage(activeRunStage)

    const runInspectorTabs = useMemo<RunInspectorTab[]>(() => {
        if (!activeRun) return []

        const tabs: RunInspectorTab[] = [{ id: 'overview', label: 'Overview' }]

        if (canUseRemoteRunLogs) {
            tabs.push({ id: 'stdout', label: 'stdout' })
            tabs.push({ id: 'stderr', label: 'stderr' })
            tabs.push({ id: 'events', label: 'Events' })
            tabs.push({ id: 'http', label: 'HTTP' })
            tabs.push({ id: 'mcp', label: 'MCP' })
        } else {
            if (activeRun.stdoutPath) tabs.push({ id: 'stdout', label: 'stdout' })
            if (activeRun.stderrPath) tabs.push({ id: 'stderr', label: 'stderr' })
            if (activeRun.eventsPath) tabs.push({ id: 'events', label: 'Events' })
            if (activeRun.httpPath) tabs.push({ id: 'http', label: 'HTTP' })
            if (activeRun.mcpPath) tabs.push({ id: 'mcp', label: 'MCP' })
        }
        if (activeRunProbeFile) {
            tabs.push({ id: 'probe', label: 'Probe' })
        }
        // §263: LiveRuntimeTrace tab — shown during non-terminal stages so developers
        // can watch runtime events as they accumulate instead of only at the end.
        if (activeRun && !isTerminalRunStage(activeRunStage)) {
            tabs.push({ id: 'live-runtime-trace', label: 'Live trace' })
        }
        if (activeRunTranscriptSessions.length > 0) {
            tabs.push({ id: 'transcript', label: 'Agent transcript' })
        }
        if (activeRunOutputFiles.length > 0) {
            tabs.push({ id: 'outputs', label: `Outputs (${activeRunOutputFiles.length})` })
        }
        if (activeRun.mcpPath) {
            tabs.push({ id: 'handlers', label: 'Handlers' })
        }
        if (activeWebhook && componentJobsClient) {
            tabs.push({ id: 'deliveries', label: 'Deliveries' })
        }

        return tabs
    }, [activeRun, activeRunOutputFiles.length, activeRunProbeFile, activeRunTranscriptSessions.length, canUseRemoteRunLogs, activeWebhook, activeRunStage, componentJobsClient])

    const preferredRunTab = useMemo(() => {
        if (!activeRunId || parsedSelection?.runId !== activeRunId) return null
        return getRunInspectorTabFromSelection(path)
    }, [activeRunId, parsedSelection?.runId, path])

    const availableRunTabIds = useMemo(
        () => new Set(runInspectorTabs.map(tab => tab.id)),
        [runInspectorTabs],
    )

    useEffect(() => {
        if (runInspectorTabs.length === 0) {
            setActiveRunTab('overview')
            return
        }

        setActiveRunTab(current => {
            if (preferredRunTab && availableRunTabIds.has(preferredRunTab)) {
                return preferredRunTab
            }
            if (availableRunTabIds.has(current)) {
                return current
            }
            return runInspectorTabs[0].id
        })
    }, [availableRunTabIds, preferredRunTab, runInspectorTabs])

    useEffect(() => {
        if (activeRunOutputFiles.length === 0) {
            setSelectedOutputPath(null)
            return
        }

        const preferredOutputPath = parsedSelection?.kind === 'run_output' && parsedSelection.runId === activeRunId
            ? parsedSelection.path
            : null

        setSelectedOutputPath(current => {
            if (preferredOutputPath && activeRunOutputFiles.some(file => file.path === preferredOutputPath)) {
                return preferredOutputPath
            }
            if (current && activeRunOutputFiles.some(file => file.path === current)) {
                return current
            }
            return activeRunOutputFiles[0].path
        })
    }, [activeRunId, activeRunOutputFiles, parsedSelection?.kind, parsedSelection?.path, parsedSelection?.runId])

    useEffect(() => {
        if (activeRunTranscriptSessions.length === 0) {
            setSelectedTranscriptPath(null)
            return
        }

        const preferredTranscriptPath = parsedSelection?.kind === 'run_output' && parsedSelection.runId === activeRunId && parseRunTranscriptSession(parsedSelection.path)
            ? parsedSelection.path
            : null

        setSelectedTranscriptPath(current => {
            if (preferredTranscriptPath && activeRunTranscriptSessions.some(session => session.path === preferredTranscriptPath)) {
                return preferredTranscriptPath
            }
            if (current && activeRunTranscriptSessions.some(session => session.path === current)) {
                return current
            }
            return activeRunTranscriptSessions[0].path
        })
    }, [activeRunId, activeRunTranscriptSessions, parsedSelection?.kind, parsedSelection?.path, parsedSelection?.runId])

    const activeOutputFile = useMemo(
        () => activeRunOutputFiles.find(file => file.path === selectedOutputPath) ?? activeRunOutputFiles[0] ?? null,
        [activeRunOutputFiles, selectedOutputPath],
    )
    const activeRunTranscriptSession = useMemo(
        () => activeRunTranscriptSessions.find(session => session.path === selectedTranscriptPath) ?? activeRunTranscriptSessions[0] ?? null,
        [activeRunTranscriptSessions, selectedTranscriptPath],
    )
    const activeRunLogName = useMemo<RunLogName | null>(() => {
        if (activeRunTab === 'handlers') {
            return 'mcp.jsonl'
        }
        return isRunLogTab(activeRunTab) ? getRunLogNameForTab(activeRunTab) : null
    }, [activeRunTab])
    const activeRunLogPath = useMemo(
        () => (activeRunPaths && activeRunLogName ? getRunLogPathForName(activeRunPaths, activeRunLogName) : null),
        [activeRunLogName, activeRunPaths],
    )

    const activeRunInspectorPath = useMemo(() => {
        if (!activeRun) return null

        switch (activeRunTab) {
            case 'overview':
                return activeRun.metadataPath
            case 'stdout':
                return activeRun.stdoutPath ?? null
            case 'stderr':
                return activeRun.stderrPath ?? null
            case 'events':
                return activeRun.eventsPath ?? null
            case 'http':
                return activeRun.httpPath ?? null
            case 'mcp':
            case 'handlers':
                return activeRun.mcpPath ?? null
            case 'probe':
                return activeRunProbeFile?.path ?? null
            // §263: live-runtime-trace is handled separately via polling in the render;
            // the inspector path is computed inline in the LiveRuntimeTrace panel.
            case 'live-runtime-trace':
                return null
            case 'transcript':
                return activeRunTranscriptSession?.path ?? null
            case 'outputs':
                return activeOutputFile?.path ?? null
            case 'deliveries':
                return null
            default:
                return activeRun.metadataPath
        }
    }, [activeOutputFile?.path, activeRun, activeRunProbeFile?.path, activeRunTab, activeRunTranscriptSession?.path])

    const activeRunInspectorPathMtime = useMemo(
        () => (activeRunInspectorPath ? files.find(file => file.path === activeRunInspectorPath)?.mtime ?? 0 : 0),
        [activeRunInspectorPath, files],
    )

    // §263: Poll the incrementally-flushed live-runtime-trace file every 1s
    // when the LiveRuntimeTrace tab is active, and reset state when switching away.
    useEffect(() => {
        if (activeRunTab !== 'live-runtime-trace' || !activeRun || !activeRunPaths) {
            setLiveTraceEvents([])
            setLiveTraceFirstEventArrived(false)
            return
        }

        let pollInterval: ReturnType<typeof setInterval> | null = null

        const pollLiveTrace = async () => {
            if (!isMountedRef.current) return
            const liveTracePath = `${activeRunPaths.outputsDir}/probe/${LIVE_RUNTIME_TRACE_FILENAME}`
            try {
                const text = await readFile(liveTracePath)
                if (!isMountedRef.current) return
                if (!text) return
                const parsed = JSON.parse(text) as { events?: RuntimeEventEntry[] }
                if (!parsed.events) return
                setLiveTraceEvents(parsed.events)
                if (parsed.events.length > 0) {
                    setLiveTraceFirstEventArrived(true)
                }
            } catch {
                // File not found yet — expected before first flush; ignore.
            }
        }

        void pollLiveTrace() // run immediately on tab switch
        pollInterval = setInterval(() => { void pollLiveTrace() }, 1000)

        return () => {
            if (pollInterval !== null) {
                clearInterval(pollInterval)
            }
            setLiveTraceEvents([])
            setLiveTraceFirstEventArrived(false)
        }
    }, [activeRunTab, activeRun?.runId, activeRunPaths?.outputsDir, readFile])

    useEffect(() => {
        const shouldLoadLocalFile = activeRunTab === 'overview' || activeRunTab === 'outputs' || !canUseRemoteRunLogs || !activeRunLogName

        if (shouldLoadLocalFile && !activeRunInspectorPath) {
            setRunInspectorContent(null)
            setRunInspectorError(null)
            setIsRunInspectorLoading(false)
            return
        }

        let cancelled = false
        const abortController = new AbortController()

        const setResolvedContent = (content: string | null) => {
            if (cancelled) return
            setRunInspectorContent(content && content.trim().length > 0 ? content : null)
            setRunInspectorError(null)
            setIsRunInspectorLoading(false)
        }

        const loadLocalInspector = async (): Promise<void> => {
            if (!activeRunInspectorPath) {
                setResolvedContent(null)
                return
            }

            try {
                const text = await readFile(activeRunInspectorPath)
                if (cancelled) return
                setResolvedContent(text)
            } catch (error) {
                if (cancelled) return
                setRunInspectorContent(null)
                setRunInspectorError(error instanceof Error ? error.message : 'Unknown error')
                setIsRunInspectorLoading(false)
            }
        }

        const loadRemoteBatchInspector = async (): Promise<void> => {
            if (!componentJobsClient || !activeRun || !activeRunLogName) {
                await loadLocalInspector()
                return
            }

            try {
                const content = await downloadRunLog({
                    ...componentJobsClient,
                    runId: activeRun.runId,
                    logName: activeRunLogName,
                    signal: abortController.signal,
                })
                if (cancelled) return
                setResolvedContent(content)
            } catch (error) {
                if (cancelled || isAbortError(error)) return
                if (isNotFoundComponentJobsError(error)) {
                    await loadLocalInspector()
                    return
                }

                if (activeRunInspectorPath) {
                    await loadLocalInspector()
                    return
                }

                setRunInspectorContent(null)
                setRunInspectorError(error instanceof Error ? error.message : 'Unknown error')
                setIsRunInspectorLoading(false)
            }
        }

        const loadRemoteTailInspector = async (): Promise<void> => {
            if (!componentJobsClient || !activeRun || !activeRunLogName) {
                await loadLocalInspector()
                return
            }

            try {
                setRunInspectorContent(null)
                setRunInspectorError(null)
                setIsRunInspectorLoading(false)

                for await (const line of tailRunLog({
                    ...componentJobsClient,
                    runId: activeRun.runId,
                    logName: activeRunLogName,
                    signal: abortController.signal,
                })) {
                    if (cancelled) return
                    setRunInspectorContent(current => appendLogLine(current, line))
                }

                if (cancelled) return
                setRunInspectorError(null)
                setIsRunInspectorLoading(false)
            } catch (error) {
                if (cancelled || isAbortError(error)) return
                await loadRemoteBatchInspector()
            }
        }

        setIsRunInspectorLoading(true)
        setRunInspectorError(null)

        if (shouldLoadLocalFile) {
            void loadLocalInspector()
        } else if (shouldTailActiveRunLogs) {
            void loadRemoteTailInspector()
        } else {
            void loadRemoteBatchInspector()
        }

        return () => {
            cancelled = true
            abortController.abort()
        }
    }, [
        activeRun,
        activeRunInspectorPath,
        activeRunInspectorPathMtime,
        activeRunLogName,
        activeRunTab,
        canUseRemoteRunLogs,
        componentJobsClient,
        readFile,
        shouldTailActiveRunLogs,
    ])

    const runInspectorLabel = getRunInspectorLabel(activeRunTab)
    const canOpenRunInspectorPath = !!activeRunInspectorPath && !!(onOpenFile || onOpenFileInEditMode)
    const runInspectorHeading = activeRunTab === 'outputs' && activeOutputFile
        ? activeOutputFile.label
        : activeRunTab === 'probe' && activeRunProbeFile
            ? activeRunProbeFile.label
        : activeRunTab === 'transcript' && activeRunTranscriptSession
            ? activeRunTranscriptSession.label
            : runInspectorLabel
    const runInspectorPathLabel = activeRunTab === 'outputs'
        ? activeOutputFile?.path ?? null
        : activeRunTab === 'probe'
            ? activeRunProbeFile?.path ?? null
        : activeRunTab === 'transcript'
            ? activeRunTranscriptSession?.path ?? null
        : activeRunLogPath ?? activeRunInspectorPath
    const runInspectorEmptyCopy = activeRunTab === 'outputs'
        ? activeOutputFile
            ? 'Selected output preview is unavailable in the panel. Open the raw file for the exact bytes.'
            : 'No output files have been materialized for this run yet.'
        : activeRunTab === 'probe'
            ? 'No browser probe bundle is available for this run yet.'
        : activeRunTab === 'transcript'
            ? 'No run-owned agent transcript bundle is available for this run yet.'
        : activeRunTab === 'overview'
            ? 'Run metadata has not been materialized yet.'
            : shouldTailActiveRunLogs
                ? `Waiting for ${runInspectorLabel.toLowerCase()} lines from the live run…`
                : `No ${runInspectorLabel.toLowerCase()} log is available for this run.`

    const openCompanionPath = (targetPath: string | undefined) => {
        if (!targetPath) return
        if (onOpenFileInEditMode) {
            onOpenFileInEditMode(targetPath)
            return
        }
        onOpenFile?.(targetPath)
    }

    const openWorkspacePath = (targetPath: string | undefined) => {
        if (!targetPath) return
        if (onOpenFile) {
            onOpenFile(targetPath)
            return
        }
        onOpenFileInEditMode?.(targetPath)
    }

    const handleSaveSettings = async (nextSettings: ComponentJobSettings) => {
        if (!companionPaths || !sourcePath) return

        setIsScheduleSaving(true)
        setControlPlaneError(null)

        try {
            if (componentJobsClient) {
                const shouldPersistServerSchedule = nextSettings.enabled && nextSettings.schedulePreset !== 'manual'

                if (shouldPersistServerSchedule) {
                    const artifactId = await resolveArtifactForServerRun()
                    await schedule({
                        ...componentJobsClient,
                        sourcePath,
                        artifactId,
                        workspaceBlobId,
                        enabled: true,
                        schedulePreset: nextSettings.schedulePreset,
                        cron: nextSettings.cron,
                        timezone: nextSettings.timezone,
                        enginePolicy: nextSettings.enginePolicy,
                        concurrencyPolicy: nextSettings.concurrencyPolicy,
                        payloadPreset: nextSettings.payloadPreset,
                    })
                } else {
                    try {
                        await disableSchedule({
                            ...componentJobsClient,
                            sourcePath,
                            workspaceBlobId,
                        })
                    } catch (error) {
                        if (!isNotFoundComponentJobsError(error)) {
                            throw error
                        }
                    }
                }
            }

            await writeFile(
                companionPaths.settings,
                `${serializeComponentJobConfig({
                    ...(configEntry ? { entry: configEntry } : {}),
                    schedule: nextSettings,
                    ...(serverRunner ? { serverRunner } : {}),
                }, { fallbackTimezone: browserTimeZone })}\n`,
            )
            setSettings(nextSettings)
            setIsScheduleOpen(false)
            toast('Saved component job settings', { type: 'success' })
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Unknown error'
            setControlPlaneError(`Failed to save schedule: ${message}`)
            toast(`Failed to save component job settings: ${message}`, { type: 'error' })
        } finally {
            if (isMountedRef.current) {
                setIsScheduleSaving(false)
            }
        }
    }

    const updateLiveBuild = (status: ComponentBuildStatusRecord, serial: number): boolean => {
        if (!isMountedRef.current || buildPollSerialRef.current !== serial) {
            return false
        }
        setLiveBuild(status)
        return true
    }

    const materializeLiveRunStatus = async (status: ComponentRunStatusRecord) => {
        if (!sourcePath) return

        const current = runMaterializationRef.current[status.runId] ?? {
            startedAt: status.startedAt,
            events: [],
        }

        const lastEvent = current.events[current.events.length - 1]
        if (!lastEvent
            || lastEvent.stage !== status.stage
            || lastEvent.message !== status.message
            || lastEvent.updatedAt !== status.updatedAt
            || lastEvent.error !== status.error) {
            current.events = current.events.concat({
                stage: status.stage,
                message: status.message,
                updatedAt: status.updatedAt,
                totalElapsedSeconds: status.totalElapsedSeconds,
                stageElapsedSeconds: status.stageElapsedSeconds,
                error: status.error,
            })
        }

        runMaterializationRef.current[status.runId] = current

        try {
            await materializeRunStatusToWorkspace({
                sourcePath,
                status,
                startedAt: current.startedAt,
                eventHistory: current.events,
                workspace: workspaceWriter,
                client: componentJobsClient,
            })
            // §263: Flush runtime events incrementally so the LiveRuntimeTrace tab
            // can poll and render them during execution, not only at the end.
            persistIncrementalRuntimeTrace(workspaceWriter, sourcePath, status.runId)
        } catch (error) {
            if (!isMountedRef.current) return
            const message = error instanceof Error ? error.message : 'Unknown error'
            setControlPlaneError(`Server run is live, but workspace history could not be updated: ${message}`)
        }
    }

    const captureFailedRunProbe = async (
        status: ComponentRunStatusRecord,
        marker: ComponentJobRunProbeCaptureMarker | null,
    ) => {
        if (!sourcePath || persistedRunProbeIdsRef.current.has(status.runId)) {
            return
        }

        try {
            const localServerMode = (() => {
                if (!currentWorkspaceEntry) return false
                try {
                    const parsed = new URL(currentWorkspaceEntry.apiBaseUrl)
                    return ['localhost', '127.0.0.1', '[::1]'].includes(parsed.hostname)
                } catch {
                    return false
                }
            })()

            const probe = await captureComponentJobRunProbe({
                engineManager,
                sourcePath,
                runId: status.runId,
                artifactId: status.artifactId,
                status: status.stage,
                stage: status.stage,
                message: status.message,
                error: status.error,
                localServerMode,
                marker,
            })

            persistComponentJobRunProbe(workspaceWriter, sourcePath, status.runId, probe)
            persistedRunProbeIdsRef.current.add(status.runId)
        } catch (error) {
            if (!isMountedRef.current) return
            const message = error instanceof Error ? error.message : 'Unknown error'
            setControlPlaneError(`Server run failed, and browser probe capture could not be saved: ${message}`)
        }
    }

    const materializeFailedBuildAttempt = async (
        status: ComponentBuildStatusRecord,
        marker: ComponentJobRunProbeCaptureMarker | null,
    ) => {
        if (!componentJobsClient || !sourcePath || persistedBuildProbeIdsRef.current.has(status.buildId)) {
            return
        }

        try {
            await materializeFailedBuildStatusToWorkspace({
                sourcePath,
                status,
                workspace: workspaceWriter,
                client: componentJobsClient,
            })

            const localServerMode = (() => {
                if (!currentWorkspaceEntry) return false
                try {
                    const parsed = new URL(currentWorkspaceEntry.apiBaseUrl)
                    return ['localhost', '127.0.0.1', '[::1]'].includes(parsed.hostname)
                } catch {
                    return false
                }
            })()

            const probe = await captureComponentJobRunProbe({
                engineManager,
                sourcePath,
                buildId: status.buildId,
                artifactId: status.artifactId,
                status: status.stage,
                stage: status.stage,
                message: status.message,
                error: status.error,
                localServerMode,
                marker,
            })

            persistComponentJobBuildProbe(workspaceWriter, sourcePath, status.buildId, probe)
            persistedBuildProbeIdsRef.current.add(status.buildId)
        } catch (error) {
            if (!isMountedRef.current) return
            const message = error instanceof Error ? error.message : 'Unknown error'
            setControlPlaneError(`Build failed, and diagnostics could not be saved: ${message}`)
        }
    }

    const updateLiveRun = (status: ComponentRunStatusRecord, serial: number): boolean => {
        if (!isMountedRef.current || runPollSerialRef.current !== serial) {
            return false
        }
        setLiveRun(status)
        void materializeLiveRunStatus(status)
        return true
    }

    const materializeReadyBuild = async (artifactId: string) => {
        if (!componentJobsClient || !sourcePath) return

        try {
            await materializeBuildArtifactToWorkspace({
                ...componentJobsClient,
                sourcePath,
                artifactId,
                workspace: workspaceWriter,
            })
        } catch (error) {
            if (!isMountedRef.current) return
            const message = error instanceof Error ? error.message : 'Unknown error'
            setControlPlaneError(`Build is ready, but workspace artifacts could not be updated: ${message}`)
        }
    }

    const pollBuildUntilSettled = async (
        initialStatus: ComponentBuildStatusRecord,
        serial: number,
    ): Promise<ComponentBuildStatusRecord> => {
        let current = initialStatus
        while (!isTerminalBuildStage(current.stage) && buildPollSerialRef.current === serial && isMountedRef.current) {
            await sleep(BUILD_POLL_INTERVAL_MS)
            if (buildPollSerialRef.current !== serial || !isMountedRef.current || !componentJobsClient) {
                break
            }

            current = await getBuildStatus({
                ...componentJobsClient,
                buildId: current.buildId,
            })
            if (!updateLiveBuild(current, serial)) {
                break
            }
        }

        return current
    }

    const pollRunUntilSettled = async (
        initialStatus: ComponentRunStatusRecord,
        serial: number,
    ): Promise<ComponentRunStatusRecord> => {
        let current = initialStatus
        while (!isTerminalRunStage(current.stage) && runPollSerialRef.current === serial && isMountedRef.current) {
            await sleep(RUN_POLL_INTERVAL_MS)
            if (runPollSerialRef.current !== serial || !isMountedRef.current || !componentJobsClient) {
                break
            }

            current = await getRunStatus({
                ...componentJobsClient,
                runId: current.runId,
            })
            if (!updateLiveRun(current, serial)) {
                break
            }
        }

        return current
    }

    const ensureRunnerBootstrap = async () => {
        if (!currentWorkspaceEntry || !companionPaths) {
            throw new Error('Component job server-runner setup is unavailable for the current workspace.')
        }

        const resolvedServerRunner = await ensureComponentJobServerRunner({
            engine: engineManager,
            workspaceEntry: currentWorkspaceEntry,
            sourceName: jobDisplayName,
            existingServerRunner: serverRunner,
        })

        if (
            !serverRunner
            || serverRunner.configBlobId !== resolvedServerRunner.configBlobId
            || serverRunner.connectionCredential !== resolvedServerRunner.connectionCredential
            || serverRunner.label !== resolvedServerRunner.label
        ) {
            await writeFile(
                companionPaths.settings,
                `${serializeComponentJobConfig({
                    ...(configEntry ? { entry: configEntry } : {}),
                    schedule: settings,
                    serverRunner: resolvedServerRunner,
                }, { fallbackTimezone: browserTimeZone })}\n`,
            )

            if (isMountedRef.current) {
                setServerRunner(resolvedServerRunner)
            }
        }

        return createComponentRunnerBootstrap(currentWorkspaceEntry, resolvedServerRunner)
    }

    const handleRefreshAppIacStatus = async () => {
        if (!appIacStatusCredential) return

        setIsAppIacStatusLoading(true)
        setAppIacStatusError(null)
        try {
            const status = await loadAppIacStatus(appIacStatusCredential)
            setAppIacStatus(status)
        } catch (error) {
            setAppIacStatusError(error instanceof Error ? error.message : 'Unknown error')
        } finally {
            if (isMountedRef.current) {
                setIsAppIacStatusLoading(false)
            }
        }
    }

    const handlePrepareAppIacStatusAccess = async () => {
        setIsAppIacPreparingAccess(true)
        setAppIacStatusError(null)
        try {
            await ensureRunnerBootstrap()
            toast('App IaC status access ready', { type: 'success' })
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Unknown error'
            setAppIacStatusError(message)
            toast(`Failed to prepare App IaC status access: ${message}`, { type: 'error' })
        } finally {
            if (isMountedRef.current) {
                setIsAppIacPreparingAccess(false)
            }
        }
    }

    const prepareBuildForSource = async (): Promise<ComponentBuildStatusRecord> => {
        if (!componentJobsClient || !sourcePath) {
            throw new Error('Component jobs control plane is unavailable for the current workspace.')
        }

        const serial = ++buildPollSerialRef.current
        setControlPlaneError(null)
        setIsBuildPending(true)
        buildProbeMarkerRef.current = createComponentJobRunProbeCaptureMarker()

        try {
            const runnerBootstrap = await ensureRunnerBootstrap()
            const started = await prepareBuild({
                ...componentJobsClient,
                sourceSnapshot: createComponentSourceSnapshot(workspaceBlobId, sourcePath, sourceMtime),
                entrypoints: [sourcePath],
                workspaceBlobId,
                runnerBootstrap,
            })

            updateLiveBuild(started, serial)
            const settled = await pollBuildUntilSettled(started, serial)

            if (buildPollSerialRef.current === serial && isMountedRef.current) {
                if (settled.stage === 'ready') {
                    if (settled.artifactId) {
                        setSelectedArtifactId(settled.artifactId)
                        await materializeReadyBuild(settled.artifactId)
                    }
                    toast(`Build ready for ${jobDisplayName}`, { type: 'success' })
                } else if (settled.stage === 'failed') {
                    await materializeFailedBuildAttempt(settled, buildProbeMarkerRef.current)
                    setSelectedFailedBuildId(settled.buildId)
                    toast(`Build failed for ${jobDisplayName}`, { type: 'error' })
                }
            }

            return settled
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Unknown error'
            if (buildPollSerialRef.current === serial && isMountedRef.current) {
                setControlPlaneError(`Build failed: ${message}`)
                toast(`Build failed: ${message}`, { type: 'error' })
            }
            throw error
        } finally {
            if (buildPollSerialRef.current === serial && isMountedRef.current) {
                setIsBuildPending(false)
            }
            buildProbeMarkerRef.current = null
        }
    }

    const resolveArtifactForServerRun = async (): Promise<string> => {
        if (liveBuild?.artifactId && liveBuild.stage === 'ready') {
            return liveBuild.artifactId
        }

        if (activeBuild?.artifactId && !latestBuildIsStale) {
            return activeBuild.artifactId
        }

        const build = await prepareBuildForSource()
        if (build.stage !== 'ready' || !build.artifactId) {
            throw new Error(build.error ?? build.message ?? 'Build did not produce a runnable artifact.')
        }

        return build.artifactId
    }

    const openStarterDialog = () => {
        if (!canWrite || starterScaffolds.length === 0) {
            return
        }
        setControlPlaneError(null)
        setIsStarterOpen(true)
    }

    const applyStarterScaffold = async (scaffold: ComponentJobScaffold) => {
        if (jobRootPath) {
            const applied = await applyStarterScaffoldToJobRoot({
                jobRootPath,
                template: scaffold.template,
                readFile,
                mkdir,
                writeFile,
            })
            setSourceText(applied.files[0]?.content ?? null)
            return applied
        }

        if (!sourcePath) {
            throw new Error('Component job starter is unavailable for this selection.')
        }

        const sourceScaffold = createSourceCompatibleComponentJobScaffold(sourcePath, scaffold.template)
        if (!sourceScaffold) {
            throw new Error('This starter requires a folder-first .job root.')
        }

        writeFile(sourcePath, sourceScaffold.files[0].content)
        setSourceText(sourceScaffold.files[0].content)
        return sourceScaffold
    }

    const handleApplyStarter = async (template: ComponentJobStarterTemplate) => {
        if (!sourcePath || !canWrite) {
            return
        }

        const scaffold = starterScaffolds.find(candidate => candidate.template === template)
        if (!scaffold) {
            return
        }

        setIsStarterApplying(true)

        try {
            const shouldConfirm = jobRootPath
                ? (await Promise.all(scaffold.files.map(file =>
                    readFile(`${jobRootPath}/${file.path}`),
                ))).some(content => typeof content === 'string' && content.trim().length > 0)
                : !isBlankComponentJobSource(await readFile(sourcePath))

            const applyNow = async () => {
                setIsStarterApplying(true)
                try {
                    setStarterConfirm(null)
                } catch {
                    // Confirmation dialog was already dismissed or errored — abort without crashing.
                    setIsStarterApplying(false)
                    return
                }
                try {
                    const applied = await applyStarterScaffold(scaffold)
                    setControlPlaneError(null)
                    toast(`${applied.label} starter ready in ${starterTargetName}`, { type: 'success' })
                } catch (error) {
                    const message = error instanceof Error ? error.message : 'Unknown error'
                    setControlPlaneError(`Starter generation failed: ${message}`)
                    toast(`Failed to create component job starter: ${message}`, { type: 'error' })
                } finally {
                    setIsStarterApplying(false)
                }
            }

            setIsStarterOpen(false)
            if (shouldConfirm) {
                setStarterConfirm({
                    title: isFolderFirstJob
                        ? `Replace starter in "${starterTargetName}"?`
                        : `Replace "${starterTargetName}"?`,
                    message: isFolderFirstJob
                        ? `This will replace scaffold-owned files with the ${scaffold.label} starter. SecureBlob will not touch job.json or .secureblob internals.`
                        : `This will replace the existing source with the ${scaffold.label} starter.`,
                    confirmLabel: 'Replace',
                    onConfirm: () => { void applyNow() },
                })
                setIsStarterApplying(false)
                return
            }

            await applyNow()
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Unknown error'
            setIsStarterApplying(false)
            setControlPlaneError(`Starter generation failed: ${message}`)
            toast(`Failed to create component job starter: ${message}`, { type: 'error' })
        }
    }

    const handleBuild = async () => {
        try {
            await prepareBuildForSource()
        } catch {
            // The build helper already surfaces the failure in panel state and toast.
        }
    }

    const handleRunOnServer = async () => {
        if (!componentJobsClient) {
            const message = 'Component jobs control plane is unavailable for the current workspace.'
            setControlPlaneError(message)
            toast(message, { type: 'error' })
            return
        }

        const serial = ++runPollSerialRef.current
        setControlPlaneError(null)
        setIsRunPending(true)

        try {
            const runnerBootstrap = await ensureRunnerBootstrap()
            const artifactId = await resolveArtifactForServerRun()
            runProbeMarkerRef.current = createComponentJobRunProbeCaptureMarker()
            const started = await runServer({
                ...componentJobsClient,
                artifactId,
                workspaceBlobId,
                engine: resolveServerEngine(settings.enginePolicy),
                runInput: resolvedRunInput ?? undefined,
                sourcePath: sourcePath ?? undefined,
                runnerBootstrap,
            })

            updateLiveRun(started, serial)
            setSelectedRunId(started.runId)
            toast(`Started server run for ${jobDisplayName}`, { type: 'success' })

            const settled = await pollRunUntilSettled(started, serial)
            if (runPollSerialRef.current === serial && isMountedRef.current) {
                if (settled.stage === 'completed') {
                    toast(`Server run completed for ${jobDisplayName}`, { type: 'success' })
                } else if (settled.stage === 'failed') {
                    await captureFailedRunProbe(settled, runProbeMarkerRef.current)
                    toast(`Server run failed for ${jobDisplayName}`, { type: 'error' })
                }
            }
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Unknown error'
            if (runPollSerialRef.current === serial && isMountedRef.current) {
                setControlPlaneError(`Server run failed: ${message}`)
                toast(`Server run failed: ${message}`, { type: 'error' })
            }
        } finally {
            if (runPollSerialRef.current === serial && isMountedRef.current) {
                setIsRunPending(false)
            }
            runProbeMarkerRef.current = null
        }
    }

    const materializeHandlerRun = async (runId: string) => {
        if (!componentJobsClient) {
            return
        }

        try {
            const status = await getRunStatus({
                ...componentJobsClient,
                runId,
            })
            await materializeLiveRunStatus(status)
            if (status.stage === 'failed') {
                await captureFailedRunProbe(status, null)
            }
        } catch (error) {
            if (!isMountedRef.current) {
                return
            }

            const message = error instanceof Error ? error.message : 'Unknown error'
            setHandlerError(`Handler run completed, but workspace history could not be updated: ${message}`)
        }
    }

    const handleInvokeHandler = async () => {
        if (!componentJobsClient || !sourcePath || !canInvokeHandler) {
            const message = 'Handler invoke is unavailable for the current workspace.'
            setHandlerError(message)
            toast(message, { type: 'error' })
            return
        }

        if (handlerInputValidation.error) {
            setHandlerError(handlerInputValidation.error)
            return
        }

        setHandlerError(null)
        setIsHandlerPending(true)

        const startedAt = Date.now()
        try {
            const runnerBootstrap = await ensureRunnerBootstrap()
            const result = await callComponentJobHandler({
                ...componentJobsClient,
                sourcePath,
                workspaceBlobId,
                engine: resolveServerEngine(settings.enginePolicy),
                inputJson: handlerInputValidation.normalizedInputJson ?? null,
                runnerBootstrap,
                timeoutSeconds: 30,
            })

            const historyItem = createHandlerInvocationHistoryItem(
                result,
                handlerInputValidation.normalizedInputJson ?? null,
                startedAt,
            )
            setHandlerInvocationHistory(current => [historyItem, ...current].slice(0, 5))
            setSelectedHandlerInvocationId(historyItem.id)

            if (result.runId) {
                void materializeHandlerRun(result.runId)
            }

            if (result.statusCode === 200) {
                toast(`Handler returned JSON output for ${jobDisplayName}`, { type: 'success' })
            } else if (result.statusCode === 204) {
                toast(`Handler completed without JSON output for ${jobDisplayName}`, { type: 'success' })
            } else {
                toast(`Handler returned HTTP ${result.statusCode} for ${jobDisplayName}`, { type: 'error' })
            }
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Unknown error'
            if (isMountedRef.current) {
                setHandlerError(`Handler invoke failed: ${message}`)
                toast(`Handler invoke failed: ${message}`, { type: 'error' })
            }
        } finally {
            if (isMountedRef.current) {
                setIsHandlerPending(false)
            }
        }
    }

    const handleCancelServerRun = async () => {
        if (!componentJobsClient || !liveRun || isTerminalRunStage(liveRun.stage)) {
            return
        }

        setIsCancelPending(true)
        setControlPlaneError(null)

        try {
            const cancelled = await cancelRun({
                ...componentJobsClient,
                runId: liveRun.runId,
            })

            setLiveRun(cancelled)
            void materializeLiveRunStatus(cancelled)
            toast(`Cancel requested for ${jobDisplayName}`, { type: 'success' })
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Unknown error'
            setControlPlaneError(`Cancel failed: ${message}`)
            toast(`Cancel failed: ${message}`, { type: 'error' })
        } finally {
            if (isMountedRef.current) {
                setIsCancelPending(false)
            }
        }
    }

    const toggleWebhookEvent = (eventName: ComponentWebhookEvent) => {
        setWebhookForm(current => {
            const isSelected = current.events.includes(eventName)
            const nextEvents = isSelected
                ? current.events.filter(candidate => candidate !== eventName)
                : [...current.events, eventName].sort((left, right) => left.localeCompare(right))

            return {
                ...current,
                events: nextEvents,
            }
        })
    }

    const handleCopyTriggerExample = async () => {
        if (!triggerRequestExample) {
            return
        }

        try {
            if (!navigator.clipboard?.writeText) {
                throw new Error('Clipboard API is unavailable in this browser.')
            }

            await navigator.clipboard.writeText(triggerRequestExample)
            setTriggerExampleCopied(true)
            toast(`Copied trigger request for ${jobDisplayName}`, { type: 'success' })
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Unknown error'
            setWebhookError(`Failed to copy trigger request: ${message}`)
            toast(`Failed to copy trigger request: ${message}`, { type: 'error' })
        }
    }

    const handleSaveWebhook = async () => {
        if (!componentJobsClient || !sourcePath) {
            const message = 'Component jobs control plane is unavailable for the current workspace.'
            setWebhookError(message)
            toast(message, { type: 'error' })
            return
        }

        setIsWebhookSaving(true)
        setWebhookError(null)

        try {
            const runnerBootstrap = await ensureRunnerBootstrap()
            const saved = await upsertWebhook({
                ...componentJobsClient,
                sourcePath,
                workspaceBlobId,
                callbackUrl: webhookForm.callbackUrl,
                secret: webhookForm.secret,
                events: webhookForm.events,
                runnerBootstrap,
            })

            setWebhooks([saved])
            setWebhookForm({
                callbackUrl: saved.callbackUrl,
                secret: '',
                events: saved.events,
            })
            toast(`${activeWebhook ? 'Updated' : 'Saved'} webhook for ${jobDisplayName}`, { type: 'success' })
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Unknown error'
            setWebhookError(`Failed to save webhook: ${message}`)
            toast(`Failed to save webhook: ${message}`, { type: 'error' })
        } finally {
            if (isMountedRef.current) {
                setIsWebhookSaving(false)
            }
        }
    }

    const handleDeleteWebhook = async (webhookId: string) => {
        if (!componentJobsClient) {
            const message = 'Component jobs control plane is unavailable for the current workspace.'
            setWebhookError(message)
            toast(message, { type: 'error' })
            return
        }

        setDeletingWebhookId(webhookId)
        setWebhookError(null)

        try {
            await deleteWebhook({
                ...componentJobsClient,
                webhookId,
            })

            setWebhooks([])
            setWebhookForm(current => ({
                ...current,
                secret: '',
            }))
            toast(`Deleted webhook for ${jobDisplayName}`, { type: 'success' })
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Unknown error'
            setWebhookError(`Failed to delete webhook: ${message}`)
            toast(`Failed to delete webhook: ${message}`, { type: 'error' })
        } finally {
            if (isMountedRef.current) {
                setDeletingWebhookId(null)
            }
        }
    }

    const structuredRunInspectorEntries = useMemo(
        () => (
            runInspectorContent && isStructuredRunLogTab(activeRunTab)
                ? formatStructuredLogEntries(activeRunTab, runInspectorContent)
                : []
        ),
        [activeRunTab, runInspectorContent],
    )

    const handlerSummaries = useMemo(
        () => (activeRunTab === 'handlers' && runInspectorContent ? formatHandlerSummaries(runInspectorContent) : []),
        [activeRunTab, runInspectorContent],
    )

    useEffect(() => {
        if (!activeFailedBuild?.probePath) {
            setBuildProbeContent(null)
            setBuildProbeError(null)
            return
        }

        let cancelled = false
        readFile(activeFailedBuild.probePath)
            .then(content => {
                if (cancelled) return
                setBuildProbeContent(content)
                setBuildProbeError(content === null ? 'Probe bundle could not be read.' : null)
            })
            .catch(error => {
                if (cancelled) return
                setBuildProbeContent(null)
                setBuildProbeError(error instanceof Error ? error.message : 'Unknown error')
            })

        return () => {
            cancelled = true
        }
    }, [activeFailedBuild?.probePath, readFile])

    const parsedRunTranscript = useMemo(
        () => (
            activeRunTab === 'transcript' && runInspectorContent
                ? parseRunTranscript(runInspectorContent)
                : { transcript: null, error: null }
        ),
        [activeRunTab, runInspectorContent],
    )
    const parsedRunProbe = useMemo(
        () => (
            activeRunTab === 'probe' && runInspectorContent
                ? parseRunProbe(runInspectorContent)
                : { probe: null, error: null }
        ),
        [activeRunTab, runInspectorContent],
    )
    const parsedBuildProbe = useMemo(
        () => (buildProbeContent ? parseRunProbe(buildProbeContent) : { probe: null, error: null }),
        [buildProbeContent],
    )

    const transcriptToolResultsById = useMemo(() => {
        const results = new Map<string, Extract<TranscriptEvent, { type: 'tool_result' }>>()
        for (const event of parsedRunTranscript.transcript?.uiEvents ?? []) {
            if (event.type === 'tool_result') {
                results.set(event.id, event)
            }
        }
        return results
    }, [parsedRunTranscript.transcript?.uiEvents])

    const deliveriesForRun = useMemo<ComponentWebhookDeliveryRecord[]>(() => {
        if (!webhookDeliveries || !activeRunId) return []
        return webhookDeliveries.deliveries.filter(d => d.runId === activeRunId)
    }, [activeRunId, webhookDeliveries])

    const renderRunInspectorPreview = () => {
        if (activeRunTab === 'probe') {
            if (isRunInspectorLoading) {
                return <p className="component-job-empty-copy">Loading {runInspectorHeading}…</p>
            }
            if (runInspectorError) {
                return <p className="component-job-form-error">Failed to load {runInspectorHeading}: {runInspectorError}</p>
            }
            if (runInspectorContent === null) {
                return <p className="component-job-empty-copy">{runInspectorEmptyCopy}</p>
            }
            return renderComponentJobProbePanel(
                parsedRunProbe.probe,
                parsedRunProbe.error,
                runInspectorContent,
                'Run failed',
            )
        }

        if (activeRunTab === 'transcript') {
            if (isRunInspectorLoading) {
                return <p className="component-job-empty-copy">Loading {runInspectorHeading}…</p>
            }
            if (runInspectorError) {
                return <p className="component-job-form-error">Failed to load {runInspectorHeading}: {runInspectorError}</p>
            }
            if (runInspectorContent === null) {
                return <p className="component-job-empty-copy">{runInspectorEmptyCopy}</p>
            }
            if (!parsedRunTranscript.transcript) {
                return (
                    <div className="component-job-transcript-panel">
                        {parsedRunTranscript.error && (
                            <p className="component-job-form-error">{parsedRunTranscript.error}</p>
                        )}
                        <pre className="component-job-log-preview">{runInspectorContent}</pre>
                    </div>
                )
            }

            const transcript = parsedRunTranscript.transcript
            const costLabel = transcript.cost
                ? `$${transcript.cost.estimatedCostUsd.toFixed(3)} · ${transcript.cost.tokensOut} out tok`
                : null
            const providerSummary = [transcript.model, transcript.providerApiMode ?? transcript.providerType]
                .filter((value): value is string => typeof value === 'string' && value.length > 0)
                .join(' · ')

            return (
                <div className="component-job-transcript-panel">
                    <div className="component-job-transcript-meta" role="list" aria-label="Agent transcript metadata">
                        <span role="listitem">Session {transcript.sessionId}</span>
                        <span role="listitem">{formatStatusLabel(transcript.status)}</span>
                        {providerSummary && <span role="listitem">{providerSummary}</span>}
                        {costLabel && <span role="listitem">{costLabel}</span>}
                        {transcript.filesModified.length > 0 && (
                            <span role="listitem">{transcript.filesModified.length} file{transcript.filesModified.length === 1 ? '' : 's'} changed</span>
                        )}
                        {transcript.attachmentCount > 0 && (
                            <span role="listitem">{transcript.attachmentCount} attachment{transcript.attachmentCount === 1 ? '' : 's'}</span>
                        )}
                        {transcript.updatedAt && <span role="listitem">Updated {timeAgo(transcript.updatedAt, { maxDepth: 'days' })}</span>}
                    </div>
                    {transcript.filesModified.length > 0 && (
                        <div className="component-job-file-links">
                            {transcript.filesModified.map(modification => (
                                <button
                                    key={`${modification.action}:${modification.path}`}
                                    type="button"
                                    className="btn btn-sm"
                                    onClick={() => openWorkspacePath(modification.path)}
                                >
                                    {modification.path}
                                </button>
                            ))}
                        </div>
                    )}
                    {transcript.uiEvents.length === 0 ? (
                        <p className="component-job-empty-copy">No semantic transcript events were captured for this session.</p>
                    ) : (
                        <div className="component-job-transcript-list">
                            {transcript.uiEvents.map((event, index) => (
                                <TranscriptMessage
                                    key={`${event.type}-${index}`}
                                    event={event}
                                    getToolResult={toolId => transcriptToolResultsById.get(toolId)}
                                    onOpenFile={openWorkspacePath}
                                />
                            ))}
                        </div>
                    )}
                </div>
            )
        }

        if (activeRunTab === 'handlers') {
            if (isRunInspectorLoading) {
                return <p className="component-job-empty-copy">Loading {runInspectorHeading}…</p>
            }
            if (runInspectorError) {
                return <p className="component-job-form-error">Failed to load {runInspectorHeading}: {runInspectorError}</p>
            }
            if (handlerSummaries.length === 0) {
                return <p className="component-job-empty-copy">No handler calls recorded for this run.</p>
            }
            return (
                <div className="component-job-jsonlog-list" role="list">
                    {handlerSummaries.map((entry, index) => (
                        <article key={`${entry.tool}-${index}`} className="component-job-jsonlog-item" role="listitem">
                            <strong>{entry.tool}</strong>
                            <span>{entry.timestamp}</span>
                            <span>{entry.duration !== null ? `${entry.duration}ms` : '—'}</span>
                            <span>{entry.status}</span>
                            {entry.requestSummary && <code>{entry.requestSummary}</code>}
                            {entry.responseSummary && <code>{entry.responseSummary}</code>}
                        </article>
                    ))}
                </div>
            )
        }

        if (activeRunTab === 'deliveries') {
            if (isWebhookDeliveriesLoading) {
                return <p className="component-job-empty-copy">Loading {runInspectorHeading}…</p>
            }
            if (deliveriesForRun.length === 0) {
                return <p className="component-job-empty-copy">No webhook deliveries found for this run.</p>
            }
            return (
                <div className="component-job-jsonlog-list" role="list">
                    {deliveriesForRun.map(delivery => (
                        <article key={delivery.deliveryId} className="component-job-jsonlog-item" role="listitem">
                            <strong>{delivery.event}</strong>
                            <span>{delivery.status}</span>
                            <span>{delivery.attemptCount} attempt{delivery.attemptCount !== 1 ? 's' : ''}</span>
                            {delivery.httpStatusCode && <span>HTTP {delivery.httpStatusCode}</span>}
                            {delivery.lastAttemptAt && <span>Last attempt: {delivery.lastAttemptAt}</span>}
                            {delivery.nextRetryAt && <span>Next retry: {delivery.nextRetryAt}</span>}
                            {delivery.lastError && <code>{delivery.lastError}</code>}
                        </article>
                    ))}
                </div>
            )
        }

        // §263: LiveRuntimeTrace — renders events from the incrementally-flushed live-trace file.
        if (activeRunTab === 'live-runtime-trace') {
            const isFinalizing = !!activeRunStage && isTerminalRunStage(activeRunStage)
            const badgeClass = liveTraceFirstEventArrived
                ? (isFinalizing ? 'component-job-live-badge component-job-live-badge--finalizing' : 'component-job-live-badge component-job-live-badge--live')
                : 'component-job-live-badge component-job-live-badge--waiting'
            const badgeLabel = liveTraceFirstEventArrived
                ? (isFinalizing ? '● Finalizing' : '● Live')
                : '○ Waiting'
            return (
                <div className="component-job-live-trace-panel">
                    <div className="component-job-live-trace-header">
                        <span className={badgeClass}>{badgeLabel}</span>
                        <span className="component-job-live-trace-count">
                            {liveTraceEvents.length} event{liveTraceEvents.length !== 1 ? 's' : ''}
                        </span>
                    </div>
                    {liveTraceEvents.length === 0 && !isFinalizing ? (
                        <p className="component-job-empty-copy">Waiting for runtime events…</p>
                    ) : (
                        <div className="component-job-jsonlog-list" role="list">
                            {liveTraceEvents.map(entry => (
                                <article key={entry.id} className="component-job-jsonlog-item" role="listitem">
                                    <span className="component-job-live-trace-time">{entry.timestamp.split('T')[1]?.replace('Z', '') ?? entry.timestamp}</span>
                                    <span className="component-job-live-trace-kind">{entry.kind}</span>
                                    <span className="component-job-live-trace-source">{entry.source}</span>
                                    <code className="component-job-live-trace-data">
                                        {JSON.stringify(entry.data).slice(0, 120)}
                                        {JSON.stringify(entry.data).length > 120 ? '…' : ''}
                                    </code>
                                </article>
                            ))}
                        </div>
                    )}
                </div>
            )
        }

        if (isRunInspectorLoading) {
            return <p className="component-job-empty-copy">Loading {runInspectorHeading}…</p>
        }
        if (runInspectorError) {
            return <p className="component-job-form-error">Failed to load {runInspectorHeading}: {runInspectorError}</p>
        }
        if (runInspectorContent === null) {
            return <p className="component-job-empty-copy">{runInspectorEmptyCopy}</p>
        }
        if (activeRunTab === 'http' && httpWaterfallMode && structuredRunInspectorEntries.length > 0) {
            return (
                <div className="component-job-jsonlog-list" role="list">
                    {structuredRunInspectorEntries.map((entry, index) => {
                        const timingBar = entry.timingBar
                        const phases: { label: string; ms: number | null }[] = [
                            { label: 'DNS', ms: timingBar?.dnsMs ?? null },
                            { label: 'Connect', ms: timingBar?.connectMs ?? null },
                            { label: 'TLS', ms: timingBar?.tlsMs ?? null },
                            { label: 'TTFB', ms: timingBar?.ttfbMs ?? null },
                            { label: 'Download', ms: timingBar?.downloadMs ?? null },
                        ]
                        const totalMs = timingBar?.totalMs ?? 0
                        return (
                            <article key={`${entry.title}-${index}`} className="component-job-jsonlog-item" role="listitem">
                                <strong>{entry.title}</strong>
                                {entry.meta.length > 0 && <span>{entry.meta.join(' · ')}</span>}
                                {timingBar && (
                                    <div className="component-job-timing-bar" role="img" aria-label={`${totalMs}ms total`}>
                                        {phases.map(phase => {
                                            const pct = totalMs > 0 && phase.ms != null ? (phase.ms / totalMs) * 100 : 0
                                            return (
                                                <span
                                                    key={phase.label}
                                                    className={`component-job-timing-phase component-job-timing-${phase.label.toLowerCase()}`}
                                                    style={{ width: `${pct.toFixed(1)}%` }}
                                                    title={phase.ms != null ? `${phase.label}: ${phase.ms}ms` : `${phase.label}: —`}
                                                />
                                            )
                                        })}
                                        {totalMs > 0 && (
                                            <span className="component-job-timing-total">{totalMs}ms</span>
                                        )}
                                    </div>
                                )}
                                {entry.detail && <code>{entry.detail}</code>}
                            </article>
                        )
                    })}
                </div>
            )
        }

        if (structuredRunInspectorEntries.length > 0) {
            return (
                <div className="component-job-jsonlog-list" role="list">
                    {structuredRunInspectorEntries.map((entry, index) => (
                        <article key={`${entry.title}-${index}`} className="component-job-jsonlog-item" role="listitem">
                            <strong>{entry.title}</strong>
                            {entry.meta.length > 0 && <span>{entry.meta.join(' · ')}</span>}
                            {entry.detail && <code>{entry.detail}</code>}
                        </article>
                    ))}
                </div>
            )
        }
        return <pre className="component-job-log-preview">{runInspectorContent}</pre>
    }

    if (!sourcePath || !companionPaths) {
        return (
            <div className="component-job-panel">
                <div className="component-job-empty">
                    <h2>Component Job</h2>
                    <p>Den valda filen matchar inte ett component-job-spår.</p>
                </div>
            </div>
        )
    }

    return (
        <div className="component-job-panel">
            <header className="component-job-header">
                <div className="component-job-header-copy">
                    <p className="component-job-kicker">Component Job</p>
                    <h1>{jobDisplayName}</h1>
                    <p className="component-job-path">{jobPathLabel}</p>
                    <div className="component-job-header-chips">
                        <span>{buildChip}</span>
                        <span>{liveRun ? 'Server' : activeRun ? formatTargetLabel(activeRun.target) : 'Server'}</span>
                        <span>{headerEngine}</span>
                        <span>{formatScheduleChip(settings)}</span>
                    </div>
                </div>
                <div className="component-job-actions">
                    <button className="btn btn-sm" onClick={handleRunOnServer} disabled={runButtonDisabled}>
                        {isRunPending ? 'Running…' : 'Run'}
                    </button>
                    <button className="btn btn-sm" onClick={handleBuild} disabled={buildButtonDisabled}>
                        {isBuildPending ? 'Building…' : 'Build'}
                    </button>
                    <button className="btn btn-sm" onClick={openStarterDialog} disabled={!canWrite || starterScaffolds.length === 0}>
                        Starter
                    </button>
                    {hasLiveServerRun && (
                        <button className="btn btn-sm" onClick={handleCancelServerRun} disabled={cancelButtonDisabled}>
                            {isCancelPending ? 'Canceling…' : 'Cancel server run'}
                        </button>
                    )}
                    <button className="btn btn-sm" onClick={() => setIsScheduleOpen(true)} disabled={!canWrite}>
                        Schedule
                    </button>
                    {isFolderFirstJob ? (
                        <button
                            className="btn btn-sm"
                            onClick={() => sourcePath && openCompanionPath(sourcePath)}
                            disabled={!sourcePath || (!onOpenFile && !onOpenFileInEditMode)}
                        >
                            Open {sourceFileName}
                        </button>
                    ) : onEditFile ? (
                        <button className="btn btn-sm" onClick={onEditFile}>
                            Edit file
                        </button>
                    ) : null}
                    {overviewPath && path !== overviewPath && (
                        <button className="btn btn-sm" onClick={() => onOpenFile?.(overviewPath)} disabled={!onOpenFile}>
                            Open overview
                        </button>
                    )}
                    {!isFolderFirstJob && path !== sourcePath && (
                        <button className="btn btn-sm" onClick={() => onOpenFile?.(sourcePath)} disabled={!onOpenFile}>
                            Open source
                        </button>
                    )}
                    {settingsExists && (
                        <button className="btn btn-sm" onClick={() => openCompanionPath(companionPaths.settings)} disabled={!onOpenFile && !onOpenFileInEditMode}>
                            {isFolderFirstJob ? 'Open config' : 'Settings'}
                        </button>
                    )}
                    {activeRun && (
                        <button className="btn btn-sm" onClick={() => openCompanionPath(activeRun.metadataPath)} disabled={!onOpenFile && !onOpenFileInEditMode}>
                            {activeRunLabel}
                        </button>
                    )}
                    {activeBuild && (
                        <button className="btn btn-sm" onClick={() => openCompanionPath(activeBuild.manifestPath)} disabled={!onOpenFile && !onOpenFileInEditMode}>
                            {activeBuildLabel}
                        </button>
                    )}
                </div>
            </header>

            {showOnboarding && (
                <section className="component-job-onboarding-card">
                    <div className="component-job-onboarding-copy">
                        <p className="component-job-kicker">Quick Start</p>
                        <h2>{onboardingTitle}</h2>
                        <p>{onboardingCopy}</p>
                    </div>

                    <div className="component-job-onboarding-actions">
                        {sourceIsEmpty && (
                            <button className="btn btn-sm" onClick={openStarterDialog} disabled={!canWrite || starterScaffolds.length === 0}>
                                Choose starter
                            </button>
                        )}
                        <button className="btn btn-sm" onClick={handleRunOnServer} disabled={runButtonDisabled}>
                            {isRunPending ? 'Running…' : 'Run on server'}
                        </button>
                        <button className="btn btn-sm" onClick={handleBuild} disabled={buildButtonDisabled}>
                            {isBuildPending ? 'Building…' : 'Build artifact'}
                        </button>
                    </div>

                    <div className="component-job-onboarding-steps">
                        <div className="component-job-onboarding-step">
                            <strong>Source</strong>
                            <span>{sourceIsEmpty ? 'Starter recommended' : 'Ready to build'}</span>
                        </div>
                        <div className="component-job-onboarding-step">
                            <strong>Setup</strong>
                            <span>{settingsExists ? 'Companion config saved' : 'Auto-created on first build'}</span>
                        </div>
                        <div className="component-job-onboarding-step">
                            <strong>Runner</strong>
                            <span>{serverRunner?.label ?? 'Provisioned automatically'}</span>
                        </div>
                    </div>
                </section>
            )}

            <div className="component-job-summary-grid">
                <section className="component-job-summary-card">
                    <p className="component-job-summary-label">Selection</p>
                    <strong>{describeSelection(path)}</strong>
                    <span>{selectionContextCopy}</span>
                </section>
                <section className="component-job-summary-card">
                    <p className="component-job-summary-label">{activeRunLabel}</p>
                    <strong>{activeRun ? `${formatTargetLabel(activeRun.target)} · ${activeRun.statusLabel}` : 'No runs yet'}</strong>
                    <span>
                        {activeRun
                            ? `${activeRun.startedAtLabel} · ${timeAgo(activeRun.startedAt, { maxDepth: 'days' })}`
                            : runHistoryEmptyCopy}
                    </span>
                </section>
                <section className="component-job-summary-card">
                    <p className="component-job-summary-label">{activeBuildLabel}</p>
                    <strong>{buildChip}</strong>
                    <span>
                        {liveBuild
                            ? `${liveBuild.message} · ${formatElapsedLabel(liveBuild.totalElapsedSeconds)}`
                            : activeBuild
                                ? latestBuildIsStale
                                    ? `Source changed after ${activeBuild.createdAtLabel}`
                                    : `${activeBuild.createdAtLabel} · ${timeAgo(activeBuild.createdAt, { maxDepth: 'days' })}`
                            : buildHistoryEmptyCopy}
                    </span>
                </section>
                <section className="component-job-summary-card">
                    <p className="component-job-summary-label">Schedule</p>
                    <strong>{scheduleSummary}</strong>
                    <span>{scheduleMeta}</span>
                </section>
                <section className="component-job-summary-card">
                    <p className="component-job-summary-label">History</p>
                    <strong>{runHistory.length} runs</strong>
                    <span>{buildHistory.length} builds</span>
                </section>
            </div>

            {isFolderFirstJob && (
                <section className="component-job-section">
                    <div className="component-job-section-header">
                        <div>
                            <h2>App IaC</h2>
                            <span>{appIacStatusSourceLabel}</span>
                        </div>
                        <div className="component-job-file-links">
                            {appIacStatusCredential ? (
                                <button
                                    className="btn btn-sm"
                                    onClick={() => void handleRefreshAppIacStatus()}
                                    disabled={isAppIacStatusLoading}
                                >
                                    {isAppIacStatusLoading ? 'Refreshing…' : 'Refresh status'}
                                </button>
                            ) : (
                                <button
                                    className="btn btn-sm"
                                    onClick={() => void handlePrepareAppIacStatusAccess()}
                                    disabled={!canWrite || isAppIacPreparingAccess}
                                >
                                    {isAppIacPreparingAccess ? 'Preparing…' : 'Prepare status access'}
                                </button>
                            )}
                        </div>
                    </div>

                    <div className="component-job-app-iac-grid">
                        <div className={`component-job-app-iac-card ${!appIacWorkspaceStatus ? 'neutral' : appIacWorkspaceStatus.appManifest.exists ? 'ok' : 'warn'}`}>
                            <p className="component-job-summary-label">Workspace install</p>
                            <strong>{isAppIacStatusLoading && !appIacStatus ? 'Inspecting…' : describeAppIacInstall(appIacStatus)}</strong>
                            <span>
                                {appIacWorkspaceStatus?.appManifest.appId
                                    ? `${appIacWorkspaceStatus.appManifest.appId} · ${appIacWorkspaceStatus.appManifest.path}`
                                    : APP_IAC_MANIFEST_PATH}
                            </span>
                        </div>

                        <div className={`component-job-app-iac-card ${!appIacStatus ? 'neutral' : appIacStatus.managedRegistrations.length ? 'ok' : 'warn'}`}>
                            <p className="component-job-summary-label">Server registry</p>
                            <strong>{isAppIacStatusLoading && !appIacStatus ? 'Inspecting…' : describeAppIacRegistration(appIacStatus)}</strong>
                            <span>
                                {appIacStatus?.managedRegistrations[0]
                                    ? `${appIacStatus.managedRegistrations[0].id} · ${appIacStatus.managedRegistrations[0].hasConfigConnectionToken ? 'credential installed' : 'credential missing'}`
                                    : 'Managed workspace registration'}
                            </span>
                        </div>

                        <div className={`component-job-app-iac-card ${!appIacCurrentJobStatus ? 'neutral' : appIacCurrentJobStatus.healthy ? 'ok' : 'warn'}`}>
                            <p className="component-job-summary-label">Current job</p>
                            <strong>{isAppIacStatusLoading && !appIacStatus ? 'Inspecting…' : describeAppIacJob(appIacStatus, jobRootPath)}</strong>
                            <span>
                                {appIacCurrentJobStatus?.artifactId
                                    ? `artifact ${shortArtifactId(appIacCurrentJobStatus.artifactId)} · ${appIacCurrentJobStatus.artifactStorePresent ? 'server store' : appIacCurrentJobStatus.artifactRehydratableFromWorkspace ? 'workspace artifact' : 'not rehydratable'}`
                                    : appIacCurrentJobStatus?.entrypoints.join(' · ') || jobRootPath}
                            </span>
                        </div>

                        {appIacStatusCards.map(card => (
                            <div key={card.id} className={`component-job-app-iac-card ${appIacStatusCardToneClass(card)}`}>
                                <p className="component-job-summary-label">{card.title}</p>
                                <strong>{card.status}</strong>
                                <span>{card.detail || card.source || 'Component job status'}</span>
                            </div>
                        ))}
                    </div>

                    {appIacCurrentJobStatus && (
                        <p className="component-job-meta">
                            {appIacCurrentJobStatus.registeredSchedule?.exists && <span>server schedule registered</span>}
                            {appIacCurrentJobStatus.registeredSchedule?.successors && appIacCurrentJobStatus.registeredSchedule.successors.length > 0 && (
                                <span>→ {appIacCurrentJobStatus.registeredSchedule.successors.length} successor{appIacCurrentJobStatus.registeredSchedule.successors.length > 1 ? 's' : ''}</span>
                            )}
                            {appIacCurrentJobStatus.sourceSnapshotMatches === true && <span>source snapshot current</span>}
                            {appIacCurrentJobStatus.sourceSnapshotMatches === false && <span>source snapshot stale</span>}
                            {appIacCurrentJobStatus.statusDocument?.lastReconciledAt && <span>reconciled {timeAgo(Date.parse(appIacCurrentJobStatus.statusDocument.lastReconciledAt), { maxDepth: 'days' })}</span>}
                            {appIacCurrentJobStatus.lastPromotedAt && <span>promoted {timeAgo(Date.parse(appIacCurrentJobStatus.lastPromotedAt), { maxDepth: 'days' })}</span>}
                            {appIacCurrentJobStatus.lastBuildId && <span>build {shortBuildId(appIacCurrentJobStatus.lastBuildId)}</span>}
                        </p>
                    )}

                    {appIacIssueSummary.length > 0 && (
                        <div className="component-job-app-iac-issues" role="list" aria-label="App IaC issues">
                            {appIacIssueSummary.map(issue => (
                                <span key={issue} role="listitem">{issue}</span>
                            ))}
                        </div>
                    )}

                    {appIacStatusError && <p className="component-job-form-error">App IaC status failed: {appIacStatusError}</p>}
                    {!appIacStatusCredential && !appIacStatusError && (
                        <p className="component-job-empty-copy">
                            Status inspection needs a config connection that can read this workspace.
                        </p>
                    )}
                </section>
            )}

            <section className="component-job-section">
                <div className="component-job-section-header">
                    <h2>Run Controls</h2>
                    <span>Server control plane</span>
                </div>

                <div className="component-job-control-grid">
                    <div className="component-job-control-card">
                        <p className="component-job-summary-label">Run Input</p>
                        <textarea
                            aria-label="Run input / payload"
                            className="component-job-input component-job-textarea component-job-inline-textarea"
                            value={runInputDraft}
                            onChange={event => setRunInputDraft(event.target.value)}
                            placeholder={settings.payloadPreset || '{"dryRun":true}'}
                            rows={5}
                            disabled={!canWrite}
                        />
                        <span className="component-job-empty-copy">
                            {resolvedRunInput
                                ? 'Run uses the payload shown above.'
                                : 'Leave this blank to run without payload or fall back to the saved schedule preset.'}
                        </span>
                    </div>

                    <div className="component-job-control-card">
                        <p className="component-job-summary-label">Live Build</p>
                        <strong>{buildChip}</strong>
                        <span>
                            {liveBuild
                                ? liveBuild.message
                                : activeBuild
                                    ? latestBuildIsStale
                                        ? 'The latest materialized artifact is stale relative to the source file.'
                                        : 'The latest materialized artifact is ready for reuse.'
                                    : 'No server build has been prepared for this source file yet.'}
                        </span>
                        {(liveBuild || activeBuild) && (
                            <p className="component-job-meta">
                                {liveBuild?.artifactId && <span>artifact {shortArtifactId(liveBuild.artifactId)}</span>}
                                {activeBuild?.artifactId && !liveBuild?.artifactId && <span>artifact {shortArtifactId(activeBuild.artifactId)}</span>}
                                {liveBuild && <span>{formatElapsedLabel(liveBuild.totalElapsedSeconds)}</span>}
                                {!liveBuild && activeBuild && <span>{activeBuild.createdAtLabel}</span>}
                                {latestBuildIsStale && !liveBuild && <span>stale</span>}
                            </p>
                        )}
                    </div>

                    <div className="component-job-control-card">
                        <p className="component-job-summary-label">Live Server Run</p>
                        <strong>{liveRun ? formatStatusLabel(liveRun.stage) : 'No live server run'}</strong>
                        <span>
                            {liveRun
                                ? liveRun.message
                                : 'Use Run to build if needed, start a server execution, and keep the current file context.'}
                        </span>
                        {liveRun && (
                            <p className="component-job-meta">
                                <span>{liveRun.engine}</span>
                                <span>{liveRun.runId}</span>
                                <span>{formatElapsedLabel(liveRun.totalElapsedSeconds)}</span>
                                {liveRun.artifactId && <span>artifact {shortArtifactId(liveRun.artifactId)}</span>}
                            </p>
                        )}
                    </div>
                </div>

                {controlPlaneError && <p className="component-job-form-error">{controlPlaneError}</p>}
            </section>

            {canInvokeHandler && (
                <section className="component-job-section">
                    <div className="component-job-section-header">
                        <h2>Invoke Handler</h2>
                        <span>Synchronous request/response</span>
                    </div>

                    <div className="component-job-control-grid">
                        <div className="component-job-control-card">
                            <p className="component-job-summary-label">Request JSON</p>
                            <strong>POST /handlers/{sourcePath.replace(/^\//, '')}</strong>
                            <span>
                                Invoke this handler-like source path with structured JSON input.
                                Leave the editor blank to call it without a request payload.
                            </span>
                            <textarea
                                aria-label="Handler input JSON"
                                className="component-job-input component-job-textarea component-job-inline-textarea"
                                value={handlerInputDraft}
                                onChange={event => {
                                    setHandlerInputDraft(event.target.value)
                                    if (handlerError) {
                                        setHandlerError(null)
                                    }
                                }}
                                placeholder="{}"
                                rows={8}
                                disabled={!canWrite || isHandlerPending}
                            />
                            <span className="component-job-empty-copy">
                                Timeout defaults to 30 seconds and uses the same workspace-scoped runner bootstrap as Run and Webhooks.
                            </span>
                            {handlerInputValidation.error && (
                                <p className="component-job-form-error">{handlerInputValidation.error}</p>
                            )}
                            <div className="component-job-file-links">
                                <button
                                    className="btn btn-sm"
                                    onClick={() => void handleInvokeHandler()}
                                    disabled={!canWrite || !componentJobsClient || isHandlerPending || !!handlerInputValidation.error}
                                >
                                    {isHandlerPending ? 'Invoking…' : 'Invoke handler'}
                                </button>
                            </div>
                        </div>

                        <div className="component-job-control-card">
                            <p className="component-job-summary-label">Response</p>
                            <strong>
                                {activeHandlerInvocation
                                    ? `HTTP ${activeHandlerInvocation.statusCode} · ${activeHandlerInvocation.durationMs}ms`
                                    : 'No invocation yet'}
                            </strong>
                            <span>
                                {activeHandlerInvocation
                                    ? formatHandlerInvocationSummary(activeHandlerInvocation)
                                    : 'Successful responses show formatted JSON output; failures keep the server response visible without leaving the workspace UI.'}
                            </span>

                            {activeHandlerInvocation ? (
                                <>
                                    <div className="component-job-preview-card">
                                        <strong>{activeHandlerInvocation.runId ? `run ${activeHandlerInvocation.runId}` : 'No run link available'}</strong>
                                        <span>
                                            request: {activeHandlerInvocation.inputSummary}
                                            {activeHandlerInvocation.errorCode ? ` · ${activeHandlerInvocation.errorCode}` : ''}
                                        </span>
                                        <code>HTTP {activeHandlerInvocation.statusCode}</code>
                                    </div>

                                    {activeHandlerInvocationOutput ? (
                                        <pre className="component-job-log-preview">{activeHandlerInvocationOutput}</pre>
                                    ) : (
                                        <p className="component-job-empty-copy">
                                            {activeHandlerInvocation.statusCode === 204
                                                ? 'No structured JSON response body was returned.'
                                                : 'The handler returned status metadata without a structured JSON response body.'}
                                        </p>
                                    )}

                                    {activeHandlerInvocation.runId && (
                                        <div className="component-job-file-links">
                                            <button
                                                className="btn btn-sm"
                                                onClick={() => {
                                                    setSelectedRunId(activeHandlerInvocation.runId ?? null)
                                                    setActiveRunTab('overview')
                                                }}
                                            >
                                                Open run in inspector
                                            </button>
                                        </div>
                                    )}
                                </>
                            ) : (
                                <p className="component-job-empty-copy">
                                    Invoke a handler to see its duration, response JSON, and any run link that can be inspected in the existing Run Inspector.
                                </p>
                            )}
                        </div>
                    </div>

                    {handlerError && <p className="component-job-form-error">{handlerError}</p>}

                    {handlerInvocationHistory.length > 0 && (
                        <>
                            <p className="component-job-summary-label">Recent invocations</p>
                            <div className="component-job-history-list">
                                {handlerInvocationHistory.map(invocation => (
                                    <button
                                        key={invocation.id}
                                        type="button"
                                        className={`component-job-history-item${activeHandlerInvocation?.id === invocation.id ? ' current' : ''}`}
                                        onClick={() => setSelectedHandlerInvocationId(invocation.id)}
                                    >
                                        <strong>{`HTTP ${invocation.statusCode} · ${invocation.durationMs}ms`}</strong>
                                        <span>
                                            {`request: ${invocation.inputSummary} · ${timeAgo(invocation.startedAt, { maxDepth: 'days' })}`}
                                            {invocation.runId ? ` · run ${invocation.runId}` : ''}
                                        </span>
                                    </button>
                                ))}
                            </div>
                        </>
                    )}
                </section>
            )}

            <section className="component-job-section">
                <div className="component-job-section-header">
                    <h2>External Integrations</h2>
                    <span>Source-first trigger + webhook control plane</span>
                </div>

                <div className="component-job-control-grid">
                    <div className="component-job-control-card">
                        <p className="component-job-summary-label">Trigger Request</p>
                        <strong>POST /triggers/{sourcePath.replace(/^\//, '')}</strong>
                        <span>{triggerExampleMeta}</span>
                        {triggerRequestExample ? (
                            <pre className="component-job-log-preview">{triggerRequestExample}</pre>
                        ) : (
                            <p className="component-job-empty-copy">
                                Connect this workspace through the SecureBlob HTTP session to generate a trigger example.
                            </p>
                        )}
                        <div className="component-job-file-links">
                            <button
                                className="btn btn-sm"
                                onClick={() => void handleCopyTriggerExample()}
                                disabled={!triggerRequestExample}
                            >
                                {triggerExampleCopied ? 'Copied' : 'Copy trigger request'}
                            </button>
                        </div>
                    </div>

                    <div className="component-job-control-card">
                        <p className="component-job-summary-label">Webhook Registration</p>
                        <strong>{activeWebhook ? 'Registered for this source' : 'No webhook registered yet'}</strong>
                        <span>Store a callback URL, subscribed events, and runner bootstrap for {jobDisplayName}.</span>

                        {isWebhooksLoading ? (
                            <p className="component-job-empty-copy">Loading webhook registrations…</p>
                        ) : activeWebhook ? (
                            <div className="component-job-preview-card">
                                <strong>{activeWebhook.callbackUrl}</strong>
                                <span>{formatWebhookEvents(activeWebhook.events)}</span>
                                <code>{activeWebhook.hasRunnerBootstrap ? 'runnerBootstrap stored' : 'runnerBootstrap missing'}</code>
                            </div>
                        ) : (
                            <p className="component-job-empty-copy">
                                Saving a webhook lets external triggers omit `runnerBootstrap` for this source path.
                            </p>
                        )}

                        <div className="component-job-form-field">
                            <label htmlFor="component-job-webhook-url">Callback URL</label>
                            <input
                                id="component-job-webhook-url"
                                className="component-job-input"
                                value={webhookForm.callbackUrl}
                                onChange={event => setWebhookForm(current => ({ ...current, callbackUrl: event.target.value }))}
                                placeholder="https://example.test/component-webhook"
                                disabled={!canWrite || isWebhookSaving || !!deletingWebhookId}
                            />
                        </div>

                        <div className="component-job-form-field">
                            <label htmlFor="component-job-webhook-secret">Secret</label>
                            <input
                                id="component-job-webhook-secret"
                                type="password"
                                className="component-job-input"
                                value={webhookForm.secret}
                                onChange={event => setWebhookForm(current => ({ ...current, secret: event.target.value }))}
                                placeholder={activeWebhook ? 'Enter a new secret to rotate it' : 'sig-secret'}
                                disabled={!canWrite || isWebhookSaving || !!deletingWebhookId}
                            />
                        </div>

                        <div className="component-job-form-field">
                            <label>Events</label>
                            <div className="component-job-preset-grid">
                                {COMPONENT_WEBHOOK_EVENTS.map(eventName => (
                                    <button
                                        key={eventName}
                                        type="button"
                                        className={`component-job-preset-btn${webhookForm.events.includes(eventName) ? ' active' : ''}`}
                                        aria-pressed={webhookForm.events.includes(eventName)}
                                        onClick={() => toggleWebhookEvent(eventName)}
                                        disabled={!canWrite || isWebhookSaving || !!deletingWebhookId}
                                    >
                                        {WEBHOOK_EVENT_LABELS[eventName]}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="component-job-file-links">
                            <button
                                className="btn btn-sm"
                                onClick={() => void handleSaveWebhook()}
                                disabled={
                                    !canWrite
                                    || !componentJobsClient
                                    || isWebhooksLoading
                                    || isWebhookSaving
                                    || !!deletingWebhookId
                                    || webhookForm.callbackUrl.trim().length === 0
                                    || webhookForm.secret.trim().length === 0
                                    || webhookForm.events.length === 0
                                }
                            >
                                {isWebhookSaving ? 'Saving…' : activeWebhook ? 'Update webhook' : 'Save webhook'}
                            </button>
                            {activeWebhook && (
                                <button
                                    className="btn btn-sm"
                                    onClick={() => void handleDeleteWebhook(activeWebhook.webhookId)}
                                    disabled={!canWrite || isWebhookSaving || !!deletingWebhookId}
                                >
                                    {deletingWebhookId === activeWebhook.webhookId ? 'Deleting…' : 'Delete webhook'}
                                </button>
                            )}
                        </div>

                        <span className="component-job-empty-copy">
                            Webhook secrets stay write-only. Save with a new secret whenever you update this registration.
                        </span>
                    </div>
                </div>

                {webhookError && <p className="component-job-form-error">{webhookError}</p>}
            </section>

            {activeRun && (
                <section className="component-job-section">
                    <div className="component-job-section-header">
                        <h2>Run Inspector</h2>
                        <span title={formatExactTime(activeRun.startedAt)}>{activeRun.startedAtLabel}</span>
                    </div>
                    <p className="component-job-meta">
                        <span>{formatTargetLabel(activeRun.target)}</span>
                        <span>{activeRun.statusLabel}</span>
                        {activeRun.engine && <span>{activeRun.engine}</span>}
                        {activeRun.artifactId && <span>artifact {shortArtifactId(activeRun.artifactId)}</span>}
                        {activeRun.summary && <span>{activeRun.summary}</span>}
                    </p>
                    <div className="component-job-file-links">
                        <button className="btn btn-sm" onClick={() => openCompanionPath(activeRun.metadataPath)} disabled={!onOpenFile && !onOpenFileInEditMode}>run.json</button>
                        {activeRun.stdoutPath && <button className="btn btn-sm" onClick={() => openCompanionPath(activeRun.stdoutPath)} disabled={!onOpenFile && !onOpenFileInEditMode}>stdout</button>}
                        {activeRun.stderrPath && <button className="btn btn-sm" onClick={() => openCompanionPath(activeRun.stderrPath)} disabled={!onOpenFile && !onOpenFileInEditMode}>stderr</button>}
                        {activeRun.eventsPath && <button className="btn btn-sm" onClick={() => openCompanionPath(activeRun.eventsPath)} disabled={!onOpenFile && !onOpenFileInEditMode}>events</button>}
                        {activeRun.httpPath && <button className="btn btn-sm" onClick={() => openCompanionPath(activeRun.httpPath)} disabled={!onOpenFile && !onOpenFileInEditMode}>http</button>}
                        {activeRun.mcpPath && <button className="btn btn-sm" onClick={() => openCompanionPath(activeRun.mcpPath)} disabled={!onOpenFile && !onOpenFileInEditMode}>mcp</button>}
                        {activeRun.outputFileCount > 0 && <span className="component-job-inline-note">{activeRun.outputFileCount} output file{activeRun.outputFileCount === 1 ? '' : 's'}</span>}
                    </div>

                    <div className="component-job-inspector-toolbar">
                        <div className="component-job-tablist" role="tablist" aria-label="Run inspector tabs">
                            {runInspectorTabs.map(tab => (
                                <button
                                    key={tab.id}
                                    type="button"
                                    role="tab"
                                    aria-selected={activeRunTab === tab.id}
                                    className={`component-job-tab${activeRunTab === tab.id ? ' active' : ''}`}
                                    onClick={() => setActiveRunTab(tab.id)}
                                >
                                    {tab.label}
                                </button>
                            ))}
                        </div>
                        {canOpenRunInspectorPath && (
                            <button className="btn btn-sm" onClick={() => openCompanionPath(activeRunInspectorPath ?? undefined)}>
                                Open raw {runInspectorLabel}
                            </button>
                        )}
                        {activeRunTab === 'http' && (
                            <button
                                className={`btn btn-sm${httpWaterfallMode ? ' active' : ''}`}
                                onClick={() => setHttpWaterfallMode(v => !v)}
                            >
                                {httpWaterfallMode ? 'Waterfall' : 'Raw'}
                            </button>
                        )}
                    </div>

                    {(activeRunTab === 'outputs' && activeRunOutputFiles.length > 0) || (activeRunTab === 'transcript' && activeRunTranscriptSessions.length > 0) ? (
                        <div className="component-job-output-grid">
                            <div className="component-job-output-list" role="list">
                                {activeRunTab === 'outputs'
                                    ? activeRunOutputFiles.map(outputFile => (
                                        <button
                                            key={outputFile.path}
                                            type="button"
                                            className={`component-job-output-item${activeOutputFile?.path === outputFile.path ? ' current' : ''}`}
                                            onClick={() => setSelectedOutputPath(outputFile.path)}
                                        >
                                            <strong>{outputFile.label}</strong>
                                            <span>{timeAgo(outputFile.mtime, { maxDepth: 'days' })}</span>
                                        </button>
                                    ))
                                    : activeRunTranscriptSessions.map(session => (
                                        <button
                                            key={session.path}
                                            type="button"
                                            className={`component-job-output-item${activeRunTranscriptSession?.path === session.path ? ' current' : ''}`}
                                            onClick={() => setSelectedTranscriptPath(session.path)}
                                        >
                                            <strong>{session.label}</strong>
                                            <span>{timeAgo(session.mtime, { maxDepth: 'days' })}</span>
                                        </button>
                                    ))}
                            </div>

                            <div className="component-job-log-panel">
                                <div className="component-job-log-header">
                                    <strong>{runInspectorHeading}</strong>
                                    {runInspectorPathLabel && <span>{runInspectorPathLabel}</span>}
                                </div>
                                {renderRunInspectorPreview()}
                            </div>
                        </div>
                    ) : (
                        <div className="component-job-log-panel">
                            <div className="component-job-log-header">
                                <strong>{runInspectorHeading}</strong>
                                {runInspectorPathLabel && <span>{runInspectorPathLabel}</span>}
                            </div>
                            {renderRunInspectorPreview()}
                        </div>
                    )}
                </section>
            )}

            {activeBuild && (
                <section className="component-job-section">
                    <div className="component-job-section-header">
                        <h2>Artifact</h2>
                        <span title={formatExactTime(activeBuild.createdAt)}>{activeBuild.createdAtLabel}</span>
                    </div>
                    <p className="component-job-meta">
                        <span>artifact {shortArtifactId(activeBuild.artifactId)}</span>
                        <span>{activeBuild.statusLabel}</span>
                        {activeBuild.recommendedServerEngine && <span>{activeBuild.recommendedServerEngine}</span>}
                        {activeBuildSizeLabel && <span>{activeBuildSizeLabel}</span>}
                        {activeBuild.worldVersion && <span>{activeBuild.worldVersion}</span>}
                    </p>
                    <div className="component-job-artifact-grid">
                        <div className="component-job-artifact-stat">
                            <p className="component-job-summary-label">Target</p>
                            <strong>{activeBuild.buildTarget ?? 'Component artifact'}</strong>
                            <span>{activeBuild.entrypoints.length > 0 ? activeBuild.entrypoints.join(' · ') : sourcePath}</span>
                        </div>
                        <div className="component-job-artifact-stat">
                            <p className="component-job-summary-label">Files</p>
                            <strong>{activeBuild.artifactFiles.length}</strong>
                            <span>{activeBuildSizeLabel ?? 'Size unavailable'}</span>
                        </div>
                        <div className="component-job-artifact-stat">
                            <p className="component-job-summary-label">Snapshot</p>
                            <strong>{activeBuild.sourceSnapshot ? shortArtifactId(activeBuild.sourceSnapshot) : 'Unknown'}</strong>
                            <span>{activeBuild.sourceSnapshot ?? 'No source snapshot recorded'}</span>
                        </div>
                    </div>
                    <div className="component-job-file-links">
                        <button className="btn btn-sm" onClick={() => openCompanionPath(activeBuild.manifestPath)} disabled={!onOpenFile && !onOpenFileInEditMode}>manifest</button>
                        {activeBuild.componentWasmPath && <button className="btn btn-sm" onClick={() => openCompanionPath(activeBuild.componentWasmPath)} disabled={!onOpenFile && !onOpenFileInEditMode}>component.wasm</button>}
                        {activeBuild.buildLogPath && <button className="btn btn-sm" onClick={() => openCompanionPath(activeBuild.buildLogPath)} disabled={!onOpenFile && !onOpenFileInEditMode}>build log</button>}
                    </div>
                    <div className="component-job-artifact-files" role="list" aria-label="Artifact files">
                        {activeBuild.artifactFiles.map(file => (
                            <button
                                key={file.path}
                                type="button"
                                role="listitem"
                                className="component-job-artifact-file"
                                onClick={() => openCompanionPath(file.workspacePath)}
                                disabled={!file.available || (!onOpenFile && !onOpenFileInEditMode)}
                                title={file.workspacePath}
                            >
                                <strong>{file.path}</strong>
                                <span>
                                    {[formatArtifactBytes(file.size), file.sha256 ? `sha256 ${file.sha256.slice(0, 12)}` : null, file.available ? null : 'not materialized']
                                        .filter((value): value is string => typeof value === 'string' && value.length > 0)
                                        .join(' · ') || 'artifact file'}
                                </span>
                            </button>
                        ))}
                    </div>
                </section>
            )}

            {activeFailedBuild && (
                <section className="component-job-section">
                    <div className="component-job-section-header">
                        <h2>Failed Build Diagnostics</h2>
                        <span title={formatExactTime(activeFailedBuild.createdAt)}>{activeFailedBuild.createdAtLabel}</span>
                    </div>
                    <p className="component-job-meta">
                        <span>{activeFailedBuildLabel}</span>
                        <span>build {shortBuildId(activeFailedBuild.buildId)}</span>
                        <span>{activeFailedBuild.statusLabel}</span>
                    </p>
                    <div className="component-job-file-links">
                        <button className="btn btn-sm" onClick={() => openCompanionPath(activeFailedBuild.metadataPath)} disabled={!onOpenFile && !onOpenFileInEditMode}>build metadata</button>
                        {activeFailedBuild.buildLogPath && <button className="btn btn-sm" onClick={() => openCompanionPath(activeFailedBuild.buildLogPath)} disabled={!onOpenFile && !onOpenFileInEditMode}>build log</button>}
                        {activeFailedBuild.probePath && <button className="btn btn-sm" onClick={() => openCompanionPath(activeFailedBuild.probePath)} disabled={!onOpenFile && !onOpenFileInEditMode}>browser probe</button>}
                    </div>
                    {(activeFailedBuild.summary || activeFailedBuild.error) && (
                        <div className="component-job-jsonlog-list" role="list">
                            <article className="component-job-jsonlog-item" role="listitem">
                                <strong>{activeFailedBuild.summary ?? 'Build failed'}</strong>
                                {activeFailedBuild.error && <code>{activeFailedBuild.error}</code>}
                            </article>
                        </div>
                    )}
                    {activeFailedBuild.probePath ? (
                        buildProbeContent ? renderComponentJobProbePanel(
                            parsedBuildProbe.probe,
                            parsedBuildProbe.error,
                            buildProbeContent,
                            'Build failed',
                        ) : buildProbeError ? (
                            <p className="component-job-form-error">Failed to load browser probe: {buildProbeError}</p>
                        ) : (
                            <p className="component-job-empty-copy">Loading browser probe…</p>
                        )
                    ) : (
                        <p className="component-job-empty-copy">No browser probe has been captured for this build attempt.</p>
                    )}
                </section>
            )}

            <div className="component-job-history-grid">
                <section className="component-job-section">
                    <div className="component-job-section-header">
                        <h2>Run History</h2>
                        <span>{runHistory.length}</span>
                    </div>
                    {runHistory.length === 0 ? (
                        <p className="component-job-empty-copy">No run history has been materialized yet.</p>
                    ) : (
                        <div className="component-job-history-list">
                            {runHistory.map(run => (
                                <button
                                    key={run.runId}
                                    type="button"
                                    className={`component-job-history-item${activeRunId === run.runId ? ' current' : ''}`}
                                    onClick={() => setSelectedRunId(run.runId)}
                                >
                                    <strong>{`${run.startedAtLabel} · ${formatTargetLabel(run.target)} · ${run.statusLabel}`}</strong>
                                    <span>
                                        {run.engine ? `${run.engine} · ` : ''}
                                        {run.summary ?? `Inspect run logs · ${timeAgo(run.startedAt, { maxDepth: 'days' })}`}
                                    </span>
                                </button>
                            ))}
                        </div>
                    )}
                </section>

                <section className="component-job-section">
                    <div className="component-job-section-header">
                        <h2>Build History</h2>
                        <span>{buildHistory.length}</span>
                    </div>
                    {buildHistory.length === 0 ? (
                        <p className="component-job-empty-copy">No build artifacts have been materialized yet.</p>
                    ) : (
                        <div className="component-job-history-list">
                            {buildHistory.map(build => (
                                <button
                                    key={build.artifactId}
                                    type="button"
                                    className={`component-job-history-item${activeBuildId === build.artifactId ? ' current' : ''}`}
                                    onClick={() => setSelectedArtifactId(build.artifactId)}
                                >
                                    <strong>{`artifact ${shortArtifactId(build.artifactId)} · ${build.statusLabel}`}</strong>
                                    <span>
                                        {build.recommendedServerEngine ? `${build.recommendedServerEngine} · ` : ''}
                                        {`${build.createdAtLabel} · ${timeAgo(build.createdAt, { maxDepth: 'days' })}`}
                                    </span>
                                </button>
                            ))}
                        </div>
                    )}
                </section>

                <section className="component-job-section">
                    <div className="component-job-section-header">
                        <h2>Failed Build Attempts</h2>
                        <span>{failedBuildHistory.length}</span>
                    </div>
                    {failedBuildHistory.length === 0 ? (
                        <p className="component-job-empty-copy">{failedBuildHistoryEmptyCopy}</p>
                    ) : (
                        <div className="component-job-history-list">
                            {failedBuildHistory.map(build => (
                                <button
                                    key={build.buildId}
                                    type="button"
                                    className={`component-job-history-item${activeFailedBuildId === build.buildId ? ' current' : ''}`}
                                    onClick={() => setSelectedFailedBuildId(build.buildId)}
                                >
                                    <strong>{`build ${shortBuildId(build.buildId)} · ${build.statusLabel}`}</strong>
                                    <span>
                                        {build.summary ? `${build.summary} · ` : ''}
                                        {`${build.createdAtLabel} · ${timeAgo(build.createdAt, { maxDepth: 'days' })}`}
                                    </span>
                                </button>
                            ))}
                        </div>
                    )}
                </section>
            </div>

            {isScheduleOpen && canWrite && (
                <ComponentJobSettingsDialog
                    sourceName={jobDisplayName}
                    initialSettings={settings}
                    isSaving={isScheduleSaving}
                    onClose={() => setIsScheduleOpen(false)}
                    onSave={handleSaveSettings}
                />
            )}
            {isStarterOpen && canWrite && starterScaffolds.length > 0 && (
                <ComponentJobStarterDialog
                    sourceName={jobDisplayName}
                    isFolderFirstJob={isFolderFirstJob}
                    scaffolds={starterScaffolds}
                    isApplying={isStarterApplying}
                    onClose={() => !isStarterApplying && setIsStarterOpen(false)}
                    onApply={template => void handleApplyStarter(template)}
                />
            )}
            {starterConfirm && canWrite && (
                <ComponentJobConfirmDialog
                    state={starterConfirm}
                    isSubmitting={isStarterApplying}
                    onCancel={() => !isStarterApplying && setStarterConfirm(null)}
                />
            )}
        </div>
    )
}

export default ComponentJobPanel
